import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import {
  Home,
  Package,
  Calendar as CalendarIcon,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Activity,
  User,
  LogOut,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  ChevronRight,
  Dumbbell,
  Map,
  CheckCircle2,
  Edit,
  Save,
  MapPin,
  Users,
  UserPlus,
  UserMinus,
  GraduationCap,
  Mountain,
  Trophy,
  PartyPopper,
  Loader2,
  MessageSquare,
  TrendingUp,
  Target,
  Apple,
  Flame,
  Star,
  Eye,
  EyeOff,
  Lock,
  Footprints,
  CalendarCheck,
  Award,
  Crown,
  Medal,
  FileText,
  Heart,
  Menu,
  History,
} from "lucide-react";
import { API } from "@/App";
import InvitationCodeCard from "@/components/InvitationCodeCard";
import TimePicker from "@/components/TimePicker";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import MessagingModule from "@/components/MessagingModule";
import { MessageCircle } from "lucide-react";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function MemberDashboardPage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation(['memberDashboard', 'common', 'reservations', 'members', "events", "packages", "admin", "surveys", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedMeasurement } = useLocalizedData();
  const [activeTab, setActiveTab] = useState("home");
  const [memberships, setMemberships] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(false); // Sayfa kapatan loading overlay
  
  // Data states
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [myRequests, setMyRequests] = useState([]); // Üyenin kendi talepleri
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  
  // YENİ 4-TAB SİSTEMİ
  // reservations: Onaylı + İptal + Tamamlanan
  // requests: İşletmeden gelen YENİ talepler (pending_member_approval)
  // pending: Üyenin yaptığı, işletme onayı bekleyen (pending_business_approval, change_pending_business)
  // changes: İşletmeden gelen değişiklik talepleri (change_pending_member)
  const [tabCounts, setTabCounts] = useState({ requests: 0, pending: 0, changes: 0 });
  const [incomingRequests, setIncomingRequests] = useState([]); // İşletmeden gelen talepler
  const [pendingReservations, setPendingReservations] = useState([]); // Üyenin bekleyenleri
  const [incomingChanges, setIncomingChanges] = useState([]); // İşletmeden gelen değişiklikler
  const [reservationSubTab, setReservationSubTab] = useState("reservations"); // reservations, requests, pending, changes
  
  // Reservation detail modal
  const [selectedReservationDetail, setSelectedReservationDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filtered trainers for member request form (filtered by package branch)
  const [filteredTrainersForRequest, setFilteredTrainersForRequest] = useState([]);
  
  // Events states
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [joiningEvent, setJoiningEvent] = useState(null);
  const [eventNotificationCount, setEventNotificationCount] = useState(0);
  const [eventNotifications, setEventNotifications] = useState([]);
  const [selectedEventDetail, setSelectedEventDetail] = useState(null);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [loadingEventDetail, setLoadingEventDetail] = useState(false);
  
  // Surveys states
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [viewingSurveyResponse, setViewingSurveyResponse] = useState(null);
  const [loadingSurveyResponse, setLoadingSurveyResponse] = useState(false);
  
  // Evaluations states
  const [evaluations, setEvaluations] = useState([]);
  const [unreadEvaluationCount, setUnreadEvaluationCount] = useState(0);
  const [evaluationsLoading, setEvaluationsLoading] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  
  // Goals & Badges states
  const [goals, setGoals] = useState([]);
  const [badges, setBadges] = useState([]);
  const [goalStats, setGoalStats] = useState(null);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [unseenBadgeCount, setUnseenBadgeCount] = useState(0);
  
  // Member Forms states
  const [pendingForms, setPendingForms] = useState([]);
  const [completedForms, setCompletedForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formAnswers, setFormAnswers] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [pendingFormCount, setPendingFormCount] = useState(0);
  const [pendingSurveyCount, setPendingSurveyCount] = useState(0);
  
  // Messages states
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // Request form
  const [requestForm, setRequestForm] = useState({
    business_id: "",
    trainer_id: "",
    room_id: "",
    branch: "",
    date: "",
    start_time: "",
    end_time: "",
    training_type: "one_on_one",
    member_package_id: "",
    notes: ""
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [editDatePickerOpen, setEditDatePickerOpen] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Change form - with full availability checks
  const [changeForm, setChangeForm] = useState({
    new_date: "",
    new_start_time: "",
    new_end_time: "",
    trainer_id: "",
    room_id: "",
    reason: ""
  });
  const [changeDatePickerOpen, setChangeDatePickerOpen] = useState(false);
  const [changeRoomAvailability, setChangeRoomAvailability] = useState(null);
  const [changeTrainerAvailability, setChangeTrainerAvailability] = useState(null);
  const [changeMemberTimeConflict, setChangeMemberTimeConflict] = useState(null);
  const [changeValidTimeSlots, setChangeValidTimeSlots] = useState([]);
  const [showExistingChangeConfirm, setShowExistingChangeConfirm] = useState(false);
  const [pendingChangeReservation, setPendingChangeReservation] = useState(null);
  const [sendingChangeRequest, setSendingChangeRequest] = useState(false);
  
  // View Reservation Modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingReservation, setViewingReservation] = useState(null);
  
  // Action loading state - bir butona basılınca diğerleri disabled olsun
  const [actionLoading, setActionLoading] = useState(null); // null, "approve", "reject", "change"
  
  // Edit request state
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editRequestForm, setEditRequestForm] = useState({
    business_id: "",
    branch: "",
    date: "",
    start_time: "",
    end_time: "",
    trainer_id: "",
    room_id: "",
    member_package_id: "",
    training_type: "",
    is_demo: false,
    notes: ""
  });
  const [editRequestType, setEditRequestType] = useState(""); // "package" or "demo"
  const [editSelectedPackageInfo, setEditSelectedPackageInfo] = useState(null);
  const [editRoomAvailability, setEditRoomAvailability] = useState(null);
  const [editTrainerAvailability, setEditTrainerAvailability] = useState(null);
  const [editMemberTimeConflict, setEditMemberTimeConflict] = useState(null);
  const [editValidTimeSlots, setEditValidTimeSlots] = useState([]);
  const [sendingEditRequest, setSendingEditRequest] = useState(false);
  
  // Cancel request modal
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [cancelRequestReason, setCancelRequestReason] = useState("");
  
  // Profile edit state
  const [profileForm, setProfileForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    birth_date: "",
    gender: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Business-specific data for request form
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchDemoDurations, setBranchDemoDurations] = useState({});
  const [rooms, setRooms] = useState([]);
  const [memberPackages, setMemberPackages] = useState([]);
  const [selectedPackageInfo, setSelectedPackageInfo] = useState(null);
  const [roomAvailability, setRoomAvailability] = useState(null);
  const [trainerAvailability, setTrainerAvailability] = useState(null);
  const [memberTimeConflict, setMemberTimeConflict] = useState(null); // Üyenin aynı saatte başka rezervasyonu var mı
  const [requestType, setRequestType] = useState(""); // "package", "card", or "demo"
  const [selectedCard, setSelectedCard] = useState(""); // Kart seçimi için
  const [loadingBusinessData, setLoadingBusinessData] = useState(false);
  const [businessWorkingHours, setBusinessWorkingHours] = useState({}); // İşletme çalışma saatleri
  const [trainerSchedules, setTrainerSchedules] = useState({}); // Antrenör çalışma programları
  const [validTimeSlots, setValidTimeSlots] = useState([]); // Seçilebilir saat aralıkları
  const [businessSettings, setBusinessSettings] = useState({}); // İşletme ayarları (online booking kontrolü için)
  
  // Generate date options (next 14 days)
  const dateOptions = [];
  for (let i = 0; i < 14; i++) {
    const date = addDays(new Date(), i);
    dateOptions.push({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "d MMMM yyyy, EEEE", { locale: getDateLocale() })
    });
  }

  useEffect(() => {
    fetchMemberships();
  }, []);

  // OPTIMIZED: Load only essential data on first load, then lazy load the rest
  useEffect(() => {
    if (memberships.length > 0) {
      // First, load essential data quickly for home page
      fetchEssentialData();
    }
  }, [selectedBusiness, memberships]);

  // CROSS-BUSINESS: Fetch pending reservations as soon as user is authenticated,
  // INDEPENDENT of memberships. This ensures that even if the request comes from
  // a business the user is not officially registered in (e.g. trainer booked an
  // external session), it still shows up in the member's dashboard.
  useEffect(() => {
    if (user?.user_id) {
      fetchTabBasedReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/member-dashboard/memberships`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMemberships(data);
        if (data.length === 1) {
          setSelectedBusiness(data[0].business_id);
        }
        // If user has memberships, data will be fetched by the next useEffect
        // If not, we need to stop loading here
        if (data.length === 0) {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching memberships:", error);
      setLoading(false);
    }
  };

  // OPTIMIZED: Only fetch essential data for home page initially
  const fetchEssentialData = async () => {
    const businessFilter = selectedBusiness !== "all" ? `?business_id=${selectedBusiness}` : "";
    const businessId = selectedBusiness === "all" ? (memberships[0]?.business_id || "") : selectedBusiness;
    const memberId = memberships.find(m => m.business_id === businessId)?.member_id || "";
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Only fetch packages, reservations, and TAB COUNTS for home page - much faster!
      const requests = [
        fetch(`${API}/member-dashboard/packages${businessFilter}`, { credentials: "include", headers }),
        fetch(`${API}/member-dashboard/reservations${businessFilter}`, { credentials: "include", headers }),
      ];
      
      // Also fetch tab counts for sidebar badge
      if (businessId && memberId) {
        requests.push(
          fetch(`${API}/reservations/tab-counts/${businessId}?viewer=member&member_id=${memberId}`, { credentials: "include" })
        );
      }
      
      const results = await Promise.all(requests);
      
      const [packagesRes, reservationsRes, tabCountsRes] = results;

      if (packagesRes.ok) setPackages(await packagesRes.json());
      if (reservationsRes.ok) setReservations(await reservationsRes.json());
      if (tabCountsRes && tabCountsRes.ok) {
        const counts = await tabCountsRes.json();
        setTabCounts(counts);
      }
      
      // Fetch ALL sidebar badge counts in parallel for immediate display
      if (businessId) {
        fetchAllBadgeCounts(businessId);
      }
      
      // CROSS-BUSINESS: Always fetch pending reservations across ALL businesses
      // so the member can see and respond to requests from businesses they are
      // not officially registered in (e.g. a trainer booking a session at
      // another gym for this member). This is user_id based, not business_id.
      fetchTabBasedReservations();
      
    } catch (error) {
      console.error("Error fetching essential data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // NEW: Fetch all badge counts for sidebar at once
  const fetchAllBadgeCounts = async (businessId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [evalCountRes, goalsRes, eventsRes, formsRes, surveysRes] = await Promise.all([
        // Evaluations unread count
        fetch(`${API}/evaluations/member/unread-count?business_id=${businessId}`, { credentials: "include", headers }),
        // Goals/Badges unseen count
        fetch(`${API}/goals/badges/member/unseen-count?business_id=${businessId}`, { credentials: "include", headers }),
        // Events notification count
        fetch(`${API}/events/member/notifications/count?business_id=${businessId}`, { credentials: "include", headers }),
        // Pending forms count
        fetch(`${API}/member-forms/member/pending?business_id=${businessId}`, { credentials: "include", headers }),
        // Pending surveys count
        fetch(`${API}/surveys/member/pending?business_id=${businessId}`, { credentials: "include", headers }),
      ]);
      
      if (evalCountRes.ok) {
        const data = await evalCountRes.json();
        setUnreadEvaluationCount(data.unread_count || 0);
      }
      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setUnseenBadgeCount(data.unseen_count || 0);
      }
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEventNotificationCount(data.count || 0);
      }
      if (formsRes.ok) {
        const data = await formsRes.json();
        setPendingForms(data);
        setPendingFormCount(data.length || 0);
      }
      if (surveysRes.ok) {
        const data = await surveysRes.json();
        setPendingSurveys(data);
        setPendingSurveyCount(data.length || 0);
      }
    } catch (error) {
      console.error("Error fetching badge counts:", error);
    }
  };

  // Fetch additional data lazily when needed
  const fetchAllData = async () => {
    const businessFilter = selectedBusiness !== "all" ? `?business_id=${selectedBusiness}` : "";
    const businessId = selectedBusiness === "all" ? (memberships[0]?.business_id || "") : selectedBusiness;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [packagesRes, paymentsRes, reservationsRes, historyRes, measurementsRes, requestsRes] = await Promise.all([
        fetch(`${API}/member-dashboard/packages${businessFilter}`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/payments${businessFilter}`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/reservations${businessFilter}`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/training-history${businessFilter}`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/measurements${businessFilter}`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/my-requests${businessFilter}`, { credentials: "include" }),
      ]);

      if (packagesRes.ok) setPackages(await packagesRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
      if (reservationsRes.ok) setReservations(await reservationsRes.json());
      if (historyRes.ok) setTrainingHistory(await historyRes.json());
      if (measurementsRes.ok) setMeasurements(await measurementsRes.json());
      if (requestsRes.ok) setMyRequests(await requestsRes.json());
      
      // Fetch tab-based reservation data (new 4-tab system)
      // CROSS-BUSINESS: Always fetch, user_id based (not business scoped)
      await fetchTabBasedReservations();
      
      // Fetch pending forms count for onboarding
      if (businessId) {
        try {
          const pendingRes = await fetch(`${API}/member-forms/member/pending?business_id=${businessId}`, {
            credentials: "include",
            headers,
          });
          if (pendingRes.ok) {
            const pendingData = await pendingRes.json();
            setPendingForms(pendingData);
            setPendingFormCount(pendingData.length);
          }
        } catch (error) {
          console.error("Error fetching pending forms:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // NEW: Fetch tab-based reservations for 4-tab system (PARALLEL)
  // CROSS-BUSINESS: This endpoint is user_id based (not business_id) so it
  // returns pending requests from ALL businesses - including ones the member
  // is not officially registered in (e.g. a trainer booking an external session).
  const fetchTabBasedReservations = async () => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      // Use the new endpoint that finds ALL pending reservations across businesses
      const myPendingRes = await fetch(`${API}/reservations/my-pending`, { headers: authHeaders });
      if (myPendingRes.ok) {
        const data = await myPendingRes.json();
        setIncomingRequests(data.requests || []);
        setPendingReservations(data.pending || []);
        setIncomingChanges(data.changes || []);
        setTabCounts({
          requests: (data.requests || []).length,
          pending: (data.pending || []).length,
          changes: (data.changes || []).length,
        });
      }
    } catch (error) {
      console.error("Error fetching tab-based reservations:", error);
    }
  };
  
  // Initialize profile form when user data changes
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "",
        surname: user.surname || "",
        email: user.email || "",
        phone: user.phone || "",
        birth_date: user.birth_date || "",
        gender: user.gender || ""
      });
    }
  }, [user]);
  
  // Handle profile save
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileForm)
      });
      
      if (response.ok) {
        toast.success(t("memberDashboard:profileUpdated"));
        // Refresh user data - trigger a page reload to update user context
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:profileUpdateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordData;
    
    if (!current_password || !new_password || !confirm_password) {
      toast.error(t('common:allFieldsRequired'));
      return;
    }
    
    if (new_password !== confirm_password) {
      toast.error(t("memberDashboard:passwordsDontMatch"));
      return;
    }
    
    if (new_password.length < 6) {
      toast.error(t("memberDashboard:passwordMinLength"));
      return;
    }
    
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/auth/change-password`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: "include",
        body: JSON.stringify(passwordData),
      });
      
      if (response.ok) {
        toast.success(t('common:passwordChangedSuccess'));
        setPasswordData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setShowPasswordChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:passwordChangeFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingPassword(false);
    }
  };

  const fetchBusinessData = async (businessId) => {
    setLoadingBusinessData(true);
    try {
      const [trainersRes, businessRes, roomsRes, packagesRes, settingsRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/rooms`, { credentials: "include" }),
        fetch(`${API}/member-dashboard/packages?business_id=${businessId}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/settings`, { credentials: "include" }),
      ]);

      if (trainersRes.ok) {
        const trainersData = await trainersRes.json();
        setTrainers(trainersData);
        
        // Antrenör çalışma programlarını al
        const schedules = {};
        for (const trainer of trainersData) {
          try {
            const scheduleRes = await fetch(`${API}/trainers/${trainer.trainer_id}/schedule`, { credentials: "include" });
            if (scheduleRes.ok) {
              schedules[trainer.trainer_id] = await scheduleRes.json();
            }
          } catch (e) {
            console.error(`Error fetching schedule for trainer ${trainer.trainer_id}:`, e);
          }
        }
        setTrainerSchedules(schedules);
      }
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        // İşletmenin kendi branşlarını al
        setBranches(businessData.branches || []);
        setBranchDemoDurations(businessData.branch_demo_durations || {});
        setBusinessWorkingHours(businessData.working_hours || {});
      }
      if (roomsRes.ok) setRooms(await roomsRes.json());
      if (packagesRes.ok) {
        const pkgData = await packagesRes.json();
        setMemberPackages(pkgData);
      }
      // İşletme ayarlarını al (online booking kontrolü için)
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setBusinessSettings(settingsData);
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
    } finally {
      setLoadingBusinessData(false);
    }
  };
  
  // Fetch events for member
  const fetchEvents = async (businessId) => {
    if (!businessId) return;
    setEventsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [upcomingRes, myEventsRes, notifCountRes] = await Promise.all([
        fetch(`${API}/events/member/upcoming?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/events/member/my-events?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/events/member/notifications/count?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (upcomingRes.ok) setUpcomingEvents(await upcomingRes.json());
      if (myEventsRes.ok) setMyEvents(await myEventsRes.json());
      if (notifCountRes.ok) {
        const data = await notifCountRes.json();
        setEventNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setEventsLoading(false);
    }
  };
  
  // Fetch event notifications
  const fetchEventNotifications = async (businessId) => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/events/member/notifications?business_id=${businessId}`, {
        credentials: "include",
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        setEventNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching event notifications:", error);
    }
  };
  
  // Mark notification as read
  const handleMarkNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API}/events/member/notifications/${notificationId}/read`, {
        method: "POST",
        credentials: "include",
        headers,
      });
      
      if (response.ok) {
        setEventNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
        setEventNotificationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };
  
  // Open event detail modal
  const handleOpenEventDetail = async (eventId) => {
    setLoadingEventDetail(true);
    setShowEventDetailModal(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/events/member/${eventId}/detail`, {
        credentials: "include",
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedEventDetail(data);
      } else {
        toast.error(t("memberDashboard:eventDetailFailed"));
        setShowEventDetailModal(false);
      }
    } catch (error) {
      console.error("Error fetching event detail:", error);
      toast.error(t('common:errorOccurred'));
      setShowEventDetailModal(false);
    } finally {
      setLoadingEventDetail(false);
    }
  };
  
  // Fetch surveys for member
  const fetchSurveys = async (businessId) => {
    if (!businessId) return;
    setSurveysLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [pendingRes, completedRes] = await Promise.all([
        fetch(`${API}/surveys/member/pending?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/surveys/member/completed?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingSurveys(pendingData);
        setPendingSurveyCount(pendingData.length);
      }
      if (completedRes.ok) setCompletedSurveys(await completedRes.json());
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setSurveysLoading(false);
    }
  };
  
  // Join event
  const handleJoinEvent = async (eventId) => {
    setJoiningEvent(eventId);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/events/member/${eventId}/join`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        toast.success(t("memberDashboard:joinedEvent"));
        // Refresh events
        const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
        if (businessId) fetchEvents(businessId);
      } else {
        const error = await response.json();
        // Handle Pydantic validation error format (array) vs simple error (string)
        const errorMsg = typeof error.detail === 'string' 
          ? error.detail 
          : Array.isArray(error.detail) 
            ? error.detail[0]?.msg || t("memberDashboard:joinFailed")
            : t("memberDashboard:joinFailed");
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setJoiningEvent(null);
    }
  };
  
  // Leave event
  const handleLeaveEvent = async (eventId) => {
    setJoiningEvent(eventId);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/events/member/${eventId}/leave`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        toast.success(t('common:leftEvent'));
        // Refresh events
        const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
        if (businessId) fetchEvents(businessId);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setJoiningEvent(null);
    }
  };
  
  // Fetch survey with questions
  const handleSelectSurvey = async (survey) => {
    setSurveyAnswers({});
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/surveys/member/${survey.survey_id}`, {
        credentials: "include",
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const fullSurvey = await response.json();
        setSelectedSurvey(fullSurvey);
      } else {
        toast.error(t('common:surveyLoadError'));
      }
    } catch (error) {
      console.error("Error fetching survey:", error);
      toast.error(t('common:surveyLoadError'));
    }
  };
  
  // Submit survey
  const handleSubmitSurvey = async () => {
    if (!selectedSurvey) return;
    
    // Validate all required questions are answered
    const unanswered = selectedSurvey.questions?.filter(
      q => q.is_required && !surveyAnswers[q.question_id]
    );
    
    if (unanswered?.length > 0) {
      toast.error(t("memberDashboard:answerAllRequired"));
      return;
    }
    
    setSubmittingSurvey(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Convert surveyAnswers object to the format expected by backend
      // Backend expects: {answers: {question_id: answer_value}}
      const answersDict = {};
      Object.entries(surveyAnswers).forEach(([qId, answer]) => {
        answersDict[qId] = answer;
      });
      
      const response = await fetch(`${API}/surveys/member/${selectedSurvey.survey_id}/respond`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answers: answersDict
        }),
      });
      
      if (response.ok) {
        toast.success(t("memberDashboard:surveyCompleted"));
        setSelectedSurvey(null);
        setSurveyAnswers({});
        // Refresh surveys
        const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
        if (businessId) fetchSurveys(businessId);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:submissionFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingSurvey(false);
    }
  };
  
  // Fetch evaluations for member
  const fetchEvaluations = async (businessId) => {
    if (!businessId) return;
    setEvaluationsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [evalRes, countRes] = await Promise.all([
        fetch(`${API}/evaluations/member?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/evaluations/member/unread-count?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (evalRes.ok) setEvaluations(await evalRes.json());
      if (countRes.ok) {
        const data = await countRes.json();
        setUnreadEvaluationCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching evaluations:", error);
    } finally {
      setEvaluationsLoading(false);
    }
  };
  
  // Fetch unread message count
  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/messages/unread/count`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching unread message count:", error);
    }
  };
  
  // Mark evaluation as read
  const handleMarkEvaluationRead = async (evaluationId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/evaluations/member/${evaluationId}/read`, {
        method: "POST",
        credentials: "include",
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        // Update local state
        setEvaluations(prev => prev.map(e => 
          e.evaluation_id === evaluationId ? { ...e, is_read: true } : e
        ));
        setUnreadEvaluationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };
  
  // Mark all evaluations as read
  const handleMarkAllRead = async () => {
    const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
    if (!businessId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/evaluations/member/mark-all-read?business_id=${businessId}`, {
        method: "POST",
        credentials: "include",
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        setEvaluations(prev => prev.map(e => ({ ...e, is_read: true })));
        setUnreadEvaluationCount(0);
        toast.success(t('common:allEvaluationsMarkedRead'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Fetch goals and badges for member
  const fetchGoalsAndBadges = async (businessId) => {
    if (!businessId) return;
    setGoalsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [goalsRes, badgesRes, statsRes, unseenRes] = await Promise.all([
        fetch(`${API}/goals/member?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/goals/badges/member?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/goals/stats/member?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/goals/badges/member/unseen-count?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (goalsRes.ok) setGoals(await goalsRes.json());
      if (badgesRes.ok) setBadges(await badgesRes.json());
      if (statsRes.ok) setGoalStats(await statsRes.json());
      if (unseenRes.ok) {
        const data = await unseenRes.json();
        setUnseenBadgeCount(data.unseen_count || 0);
      }
    } catch (error) {
      console.error("Error fetching goals:", error);
    } finally {
      setGoalsLoading(false);
    }
  };
  
  // Mark badges as seen
  const handleMarkBadgesSeen = async () => {
    const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
    if (!businessId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API}/goals/badges/member/mark-seen?business_id=${businessId}`, {
        method: "POST",
        credentials: "include",
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setUnseenBadgeCount(0);
    } catch (error) {
      console.error("Error marking badges seen:", error);
    }
  };
  
  // Fetch member forms
  const fetchMemberForms = async (businessId) => {
    if (!businessId) return;
    setFormsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [pendingRes, completedRes] = await Promise.all([
        fetch(`${API}/member-forms/member/pending?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/member-forms/member/completed?business_id=${businessId}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingForms(data);
        setPendingFormCount(data.length);
      }
      if (completedRes.ok) setCompletedForms(await completedRes.json());
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setFormsLoading(false);
    }
  };
  
  // Fetch form for filling
  const handleSelectForm = async (form) => {
    setFormAnswers({});
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/member-forms/member/${form.form_id}`, {
        credentials: "include",
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const fullForm = await response.json();
        setSelectedForm(fullForm);
      } else {
        toast.error(t('common:formLoadError'));
      }
    } catch (error) {
      console.error("Error fetching form:", error);
      toast.error(t('common:formLoadError'));
    }
  };
  
  // Submit form response
  const handleSubmitForm = async () => {
    if (!selectedForm) return;
    
    // Validate required questions - properly handle boolean false values
    const unanswered = selectedForm.questions?.filter(
      q => {
        if (!q.is_required) return false;
        const answer = formAnswers[q.question_id];
        // For boolean type, false is a valid answer
        if (q.type === "boolean") {
          return answer === undefined || answer === null;
        }
        // For other types, check for undefined, null, or empty string
        return answer === undefined || answer === null || answer === "";
      }
    );
    
    if (unanswered?.length > 0) {
      toast.error(t("memberDashboard:answerAllRequired"));
      return;
    }
    
    setSubmittingForm(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/member-forms/member/${selectedForm.form_id}/respond`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers: formAnswers }),
      });
      
      if (response.ok) {
        toast.success(t("memberDashboard:formSaved"));
        setSelectedForm(null);
        setFormAnswers({});
        const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
        if (businessId) fetchMemberForms(businessId);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:formSubmitFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingForm(false);
    }
  };
  
  // View completed form
  const handleViewCompletedForm = async (form) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/member-forms/member/${form.form_id}/response`, {
        credentials: "include",
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedForm({
          ...data,
          viewOnly: true, // Mark as view-only for completed forms
        });
        setFormAnswers(data.answers || {});
      } else {
        toast.error(t('common:formLoadError'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Update form response
  const handleUpdateForm = async () => {
    if (!selectedForm) return;
    
    setSubmittingForm(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/member-forms/member/${selectedForm.form_id}/response`, {
        method: "PUT",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          answers: formAnswers,
          update_reason: t("memberDashboard:updatedByMember")
        }),
      });
      
      if (response.ok) {
        toast.success(t("memberDashboard:formUpdated"));
        setSelectedForm(null);
        setFormAnswers({});
        const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
        if (businessId) fetchMemberForms(businessId);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:formUpdateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingForm(false);
    }
  };
  
  // Form icon mapping
  const FORM_ICONS = {
    FileText: FileText,
    Heart: Heart,
    User: User,
    ClipboardList: ClipboardList,
  };
  
  // Badge icon mapping
  const BADGE_ICONS = {
    footprints: Footprints,
    dumbbell: Dumbbell,
    target: Target,
    flame: Flame,
    trophy: Trophy,
    "calendar-check": CalendarCheck,
    award: Award,
    crown: Crown,
    medal: Medal,
  };
  
  // Badge color mapping
  const BADGE_COLORS = {
    green: "bg-green-100 text-green-700 border-green-300",
    blue: "bg-blue-100 text-blue-700 border-blue-300",
    purple: "bg-purple-100 text-purple-700 border-purple-300",
    orange: "bg-orange-100 text-orange-700 border-orange-300",
    gold: "bg-yellow-100 text-yellow-700 border-yellow-300",
    teal: "bg-teal-100 text-teal-700 border-teal-300",
  };
  
  // Goal progress helper
  const getGoalProgress = (goal) => {
    return Math.min(100, Math.round((goal.current_value / goal.target_value) * 100));
  };
  
  // Evaluation category helpers
  const EVAL_CATEGORY_ICONS = {
    general: ClipboardList,
    progress: TrendingUp,
    technique: Target,
    nutrition: Apple,
    motivation: Flame,
  };
  
  const EVAL_CATEGORY_COLORS = {
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    purple: "bg-purple-100 text-purple-700",
    orange: "bg-orange-100 text-orange-700",
    red: "bg-red-100 text-red-700",
  };
  
  // NEW: Filter trainers based on selected package's branch
  // Paket seçildiğinde, o paket branşını bilen antrenörler filtrelenir
  useEffect(() => {
    if (requestType === "package" && selectedPackageInfo) {
      const packageBranch = selectedPackageInfo.package_branch || selectedPackageInfo.branch || "";
      if (packageBranch) {
        const filtered = trainers.filter(t => {
          if (t.is_active === false) return false;
          const trainerBranches = t.branches || [];
          return trainerBranches.includes(packageBranch);
        });
        setFilteredTrainersForRequest(filtered);
        
        // Reset trainer if current selection doesn't know this branch
        if (requestForm.trainer_id) {
          const currentTrainer = trainers.find(t => t.trainer_id === requestForm.trainer_id);
          if (currentTrainer && !(currentTrainer.branches || []).includes(packageBranch)) {
            setRequestForm(prev => ({ ...prev, trainer_id: "" }));
          }
        }
      } else {
        setFilteredTrainersForRequest(trainers.filter(t => t.is_active !== false));
      }
    } else if (requestType === "demo" && requestForm.branch) {
      // Demo için de branşa göre filtrele
      const filtered = trainers.filter(t => {
        if (t.is_active === false) return false;
        const trainerBranches = t.branches || [];
        return trainerBranches.includes(requestForm.branch);
      });
      setFilteredTrainersForRequest(filtered);
      
      // Reset trainer if current selection doesn't know this branch
      if (requestForm.trainer_id) {
        const currentTrainer = trainers.find(t => t.trainer_id === requestForm.trainer_id);
        if (currentTrainer && !(currentTrainer.branches || []).includes(requestForm.branch)) {
          setRequestForm(prev => ({ ...prev, trainer_id: "" }));
        }
      }
    } else {
      setFilteredTrainersForRequest(trainers.filter(t => t.is_active !== false));
    }
  }, [requestType, selectedPackageInfo, requestForm.branch, trainers]);
  
  // Auto calculate end time based on package duration or demo duration
  useEffect(() => {
    if (requestForm.start_time && requestForm.branch) {
      let duration = 0;
      
      if (requestType === "package" && selectedPackageInfo?.session_duration) {
        duration = selectedPackageInfo.session_duration;
      } else if (requestType === "demo") {
        // Demo seçildiyse işletmenin o branş için demo süresini kullan
        duration = branchDemoDurations[requestForm.branch] || 30;
      } else if (requestType === "card") {
        // Kart için branşa göre seans süresi (varsayılan 60 dk)
        duration = branchDemoDurations[requestForm.branch] || 60;
      }
      
      if (duration > 0) {
        const [hours, mins] = requestForm.start_time.split(":").map(Number);
        const startMinutes = hours * 60 + mins;
        const endMinutes = startMinutes + duration;
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const newEndTime = `${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`;
      setRequestForm(prev => ({ ...prev, end_time: newEndTime }));
      }
    }
  }, [requestForm.start_time, requestForm.branch, selectedPackageInfo, requestType, branchDemoDurations]);
  
  // Fetch events, surveys, evaluations, goals, forms when tab changes
  useEffect(() => {
    if (memberships.length > 0) {
      const businessId = selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness;
      if (businessId) {
        if (activeTab === "events") {
          fetchEvents(businessId);
          fetchEventNotifications(businessId);
        } else if (activeTab === "surveys") {
          fetchSurveys(businessId);
        } else if (activeTab === "evaluations") {
          fetchEvaluations(businessId);
        } else if (activeTab === "goals") {
          fetchGoalsAndBadges(businessId);
          // Mark badges as seen when entering the goals tab
          handleMarkBadgesSeen();
        } else if (activeTab === "forms") {
          fetchMemberForms(businessId);
        } else if (activeTab === "reservations") {
          // Load full reservation data when user navigates to reservations tab
          fetchAllData();
        } else if (activeTab === "history") {
          // Load history data lazily
          fetchAllData();
        }
      }
    }
  }, [activeTab, memberships, selectedBusiness]);
  
  // Fetch unread message count on mount and poll
  useEffect(() => {
    fetchUnreadMessageCount();
    const interval = setInterval(fetchUnreadMessageCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Check room availability
  useEffect(() => {
    if (requestForm.room_id && requestForm.start_time && requestForm.date && requestForm.end_time) {
      checkRoomAvailability();
    } else {
      setRoomAvailability(null);
    }
  }, [requestForm.room_id, requestForm.start_time, requestForm.end_time, requestForm.date]);

  // Check trainer availability - training_type değiştiğinde de kontrol et
  useEffect(() => {
    if (requestForm.trainer_id && requestForm.start_time && requestForm.date && requestForm.end_time) {
      checkTrainerAvailability();
    } else {
      setTrainerAvailability(null);
    }
  }, [requestForm.trainer_id, requestForm.start_time, requestForm.end_time, requestForm.date, requestForm.training_type, requestForm.room_id]);

  const checkRoomAvailability = async () => {
    try {
      let url = `${API}/rooms/${requestForm.room_id}/availability?date=${requestForm.date}&time=${requestForm.start_time}`;
      if (requestForm.end_time) url += `&end_time=${requestForm.end_time}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setRoomAvailability(await response.json());
      }
    } catch (error) {
      console.error("Error checking room availability:", error);
    }
  };

  const checkTrainerAvailability = async () => {
    try {
      let url = `${API}/trainers/${requestForm.trainer_id}/availability?date=${requestForm.date}&time=${requestForm.start_time}`;
      if (requestForm.end_time) url += `&end_time=${requestForm.end_time}`;
      // Training type'ı gönder - düet/grup için kapasite kontrolü
      if (requestForm.training_type) url += `&training_type=${encodeURIComponent(requestForm.training_type)}`;
      if (requestForm.room_id) url += `&room_id=${encodeURIComponent(requestForm.room_id)}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setTrainerAvailability(await response.json());
      }
    } catch (error) {
      console.error("Error checking trainer availability:", error);
    }
  };

  // Check if member has conflicting reservations at the same time
  const checkMemberTimeConflict = async () => {
    if (!requestForm.date || !requestForm.start_time || !requestForm.end_time) {
      setMemberTimeConflict(null);
      return;
    }
    
    try {
      // Get member's reservations for this date
      const existingReservations = reservations.filter(r => 
        r.date === requestForm.date && 
        r.status !== "cancelled" &&
        r.status !== "rejected"
      );
      
      // Also check pending requests (myRequests)
      const existingRequests = myRequests.filter(r => 
        r.date === requestForm.date && 
        (r.status === "pending" || r.status === "waiting")
      );
      
      // Check for time overlap
      const [startH, startM] = requestForm.start_time.split(":").map(Number);
      const [endH, endM] = requestForm.end_time.split(":").map(Number);
      const newStartMinutes = startH * 60 + startM;
      const newEndMinutes = endH * 60 + endM;
      
      // Check reservations
      for (const res of existingReservations) {
        const [resStartH, resStartM] = (res.start_time || "00:00").split(":").map(Number);
        const [resEndH, resEndM] = (res.end_time || "23:59").split(":").map(Number);
        const resStartMinutes = resStartH * 60 + resStartM;
        const resEndMinutes = resEndH * 60 + resEndM;
        
        // Check overlap: new reservation overlaps with existing
        if (newStartMinutes < resEndMinutes && newEndMinutes > resStartMinutes) {
          setMemberTimeConflict({
            has_conflict: true,
            conflicting_reservation: res,
            message: `Bu saatte zaten bir rezervasyonunuz var (${res.start_time}-${res.end_time})`
          });
          return;
        }
      }
      
      // Check pending requests
      for (const req of existingRequests) {
        const [reqStartH, reqStartM] = (req.start_time || "00:00").split(":").map(Number);
        const [reqEndH, reqEndM] = (req.end_time || "23:59").split(":").map(Number);
        const reqStartMinutes = reqStartH * 60 + reqStartM;
        const reqEndMinutes = reqEndH * 60 + reqEndM;
        
        if (newStartMinutes < reqEndMinutes && newEndMinutes > reqStartMinutes) {
          setMemberTimeConflict({
            has_conflict: true,
            conflicting_request: req,
            message: `Bu saatte zaten bir talebiniz var (${req.start_time}-${req.end_time})`
          });
          return;
        }
      }
      
      setMemberTimeConflict({ has_conflict: false });
    } catch (error) {
      console.error("Error checking member time conflict:", error);
      setMemberTimeConflict(null);
    }
  };

  // Check member time conflict when date/time changes
  useEffect(() => {
    if (requestForm.date && requestForm.start_time && requestForm.end_time) {
      checkMemberTimeConflict();
    } else {
      setMemberTimeConflict(null);
    }
  }, [requestForm.date, requestForm.start_time, requestForm.end_time, reservations, myRequests]);

  // Change Modal: Calculate valid time slots
  const getChangeValidTimeSlots = () => {
    if (!changeForm.new_date || !changeForm.trainer_id) return [];
    
    const dayOfWeek = new Date(changeForm.new_date).getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    
    const timeToMins = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    const businessDay = businessWorkingHours[dayName];
    const businessOpen = businessDay?.open || "08:00";
    const businessClose = businessDay?.close || "22:00";
    const businessClosed = businessDay?.closed === true;
    
    if (businessClosed) return [];
    
    let workStart = timeToMins(businessOpen);
    let workEnd = timeToMins(businessClose);
    
    const trainerDaySchedules = trainerSchedules[changeForm.trainer_id] || [];
    const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
    
    if (trainerDaySchedules.length > 0 && !trainerDaySchedule) return [];
    
    if (trainerDaySchedule) {
      const trainerStart = timeToMins(trainerDaySchedule.start_time);
      const trainerEnd = timeToMins(trainerDaySchedule.end_time);
      workStart = Math.max(workStart, trainerStart);
      workEnd = Math.min(workEnd, trainerEnd);
    }
    
    const slots = [];
    for (let mins = workStart; mins < workEnd; mins += 30) {
      const hour = Math.floor(mins / 60);
      const minute = mins % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  };

  // Change Modal: Update valid time slots
  useEffect(() => {
    if (showChangeModal && changeForm.trainer_id) {
      let slots = getChangeValidTimeSlots();
      
      // Mevcut başlangıç saatini slot listesine dahil et (eğer yoksa)
      if (changeForm.new_start_time && !slots.includes(changeForm.new_start_time)) {
        slots = [...slots, changeForm.new_start_time].sort();
      }
      
      setChangeValidTimeSlots(slots);
    }
  }, [changeForm.new_date, changeForm.trainer_id, businessWorkingHours, trainerSchedules, showChangeModal]);

  // Change Modal: Check room availability
  const checkChangeRoomAvailability = async () => {
    if (!changeForm.room_id || !changeForm.new_date || !changeForm.new_start_time) return;
    try {
      let url = `${API}/rooms/${changeForm.room_id}/availability?date=${changeForm.new_date}&time=${changeForm.new_start_time}`;
      if (changeForm.new_end_time) url += `&end_time=${changeForm.new_end_time}`;
      if (selectedReservation?.reservation_id) url += `&exclude_reservation_id=${selectedReservation.reservation_id}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setChangeRoomAvailability(await response.json());
      }
    } catch (error) {
      console.error("Error checking change room availability:", error);
    }
  };

  // Change Modal: Check trainer availability
  const checkChangeTrainerAvailability = async () => {
    if (!changeForm.trainer_id || !changeForm.new_date || !changeForm.new_start_time) return;
    try {
      let url = `${API}/trainers/${changeForm.trainer_id}/availability?date=${changeForm.new_date}&time=${changeForm.new_start_time}`;
      if (changeForm.new_end_time) url += `&end_time=${changeForm.new_end_time}`;
      if (selectedReservation?.reservation_id) url += `&exclude_reservation_id=${selectedReservation.reservation_id}`;
      // Training type ve room_id ekle
      if (selectedReservation?.training_type) url += `&training_type=${encodeURIComponent(selectedReservation.training_type)}`;
      if (changeForm.room_id) url += `&room_id=${encodeURIComponent(changeForm.room_id)}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setChangeTrainerAvailability(await response.json());
      }
    } catch (error) {
      console.error("Error checking change trainer availability:", error);
    }
  };

  // Change Modal: Check member time conflict
  const checkChangeMemberTimeConflict = async () => {
    if (!changeForm.new_date || !changeForm.new_start_time || !changeForm.new_end_time) {
      setChangeMemberTimeConflict(null);
      return;
    }

    try {
      const [startH, startM] = changeForm.new_start_time.split(":").map(Number);
      const [endH, endM] = changeForm.new_end_time.split(":").map(Number);
      const newStart = startH * 60 + startM;
      const newEnd = endH * 60 + endM;
      
      // Check against current reservations (exclude the one being changed)
      const conflictingRes = reservations.find(r => {
        if (r.date !== changeForm.new_date) return false;
        if (r.reservation_id === selectedReservation?.reservation_id) return false;
        if (r.status === "cancelled" || r.status === "completed") return false;
        
        // Must have valid start_time to check
        if (!r.start_time) return false;
        
        const [existStartH, existStartM] = r.start_time.split(":").map(Number);
        const [existEndH, existEndM] = (r.end_time || "23:59").split(":").map(Number);
        const existStart = existStartH * 60 + existStartM;
        const existEnd = existEndH * 60 + existEndM;
        
        // Check actual time overlap
        return newStart < existEnd && newEnd > existStart;
      });
      
      // Check against pending requests (exclude change requests for this reservation)
      const conflictingReq = myRequests.find(r => {
        if (r.date !== changeForm.new_date) return false;
        if (r.status !== "pending") return false;
        // Exclude if this is the change request for the same reservation
        if (r.reservation_id === selectedReservation?.reservation_id) return false;
        
        // Must have valid start_time to check
        if (!r.start_time) return false;
        
        const [existStartH, existStartM] = r.start_time.split(":").map(Number);
        const [existEndH, existEndM] = (r.end_time || "23:59").split(":").map(Number);
        const existStart = existStartH * 60 + existStartM;
        const existEnd = existEndH * 60 + existEndM;
        
        // Check actual time overlap
        return newStart < existEnd && newEnd > existStart;
      });
      
      if (conflictingRes) {
        setChangeMemberTimeConflict({
          has_conflict: true,
          message: `${t("memberDashboard:timeConflictMessage")} (${conflictingRes.start_time}-${conflictingRes.end_time})`
        });
        return;
      }
      
      if (conflictingReq) {
        setChangeMemberTimeConflict({
          has_conflict: true,
          message: `Bu saatte bekleyen bir talebiniz var (${conflictingReq.start_time}-${conflictingReq.end_time})`
        });
        return;
      }
      
      setChangeMemberTimeConflict({ has_conflict: false });
    } catch (error) {
      console.error("Error checking change member time conflict:", error);
      setChangeMemberTimeConflict(null);
    }
  };

  // Change Modal: Check all availabilities when form changes
  useEffect(() => {
    if (showChangeModal && changeForm.room_id && changeForm.new_start_time && changeForm.new_date && changeForm.new_end_time) {
      checkChangeRoomAvailability();
    }
  }, [changeForm.room_id, changeForm.new_start_time, changeForm.new_end_time, changeForm.new_date, showChangeModal]);

  useEffect(() => {
    if (showChangeModal && changeForm.trainer_id && changeForm.new_start_time && changeForm.new_date && changeForm.new_end_time) {
      checkChangeTrainerAvailability();
    }
  }, [changeForm.trainer_id, changeForm.new_start_time, changeForm.new_end_time, changeForm.new_date, showChangeModal]);

  useEffect(() => {
    if (showChangeModal && changeForm.new_date && changeForm.new_start_time && changeForm.new_end_time) {
      checkChangeMemberTimeConflict();
    }
  }, [changeForm.new_date, changeForm.new_start_time, changeForm.new_end_time, showChangeModal]);

  // Calculate valid time slots based on business hours and trainer schedule
  const getValidTimeSlots = () => {
    if (!requestForm.date || !requestForm.trainer_id) return [];
    
    const dayOfWeek = new Date(requestForm.date).getDay(); // 0=Sunday, 6=Saturday
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    
    // Helper to convert time to minutes
    const timeToMins = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    // 1. Get business working hours for this day
    const businessDay = businessWorkingHours[dayName];
    const businessOpen = businessDay?.open || "08:00";
    const businessClose = businessDay?.close || "22:00";
    const businessClosed = businessDay?.closed === true;
    
    if (businessClosed) {
      return []; // Business is closed
    }
    
    let workStart = timeToMins(businessOpen);
    let workEnd = timeToMins(businessClose);
    
    // 2. Check trainer schedule for this day
    const trainerDaySchedules = trainerSchedules[requestForm.trainer_id] || [];
    const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
    
    if (trainerDaySchedules.length > 0 && !trainerDaySchedule) {
      // Trainer has schedules but not for this day - they don't work today
      return [];
    }
    
    if (trainerDaySchedule) {
      // Trainer has specific schedule for this day
      const trainerStart = timeToMins(trainerDaySchedule.start_time);
      const trainerEnd = timeToMins(trainerDaySchedule.end_time);
      
      // Use the intersection of business and trainer hours
      workStart = Math.max(workStart, trainerStart);
      workEnd = Math.min(workEnd, trainerEnd);
    }
    
    // Generate time slots
    const slots = [];
    for (let mins = workStart; mins < workEnd; mins += 30) {
      const hour = Math.floor(mins / 60);
      const minute = mins % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  };
  
  // Update valid time slots when date or trainer changes
  useEffect(() => {
    const slots = getValidTimeSlots();
    setValidTimeSlots(slots);
    
    // Reset start_time if it's not in valid slots
    if (requestForm.start_time && slots.length > 0 && !slots.includes(requestForm.start_time)) {
      setRequestForm(prev => ({ ...prev, start_time: "" }));
    }
  }, [requestForm.date, requestForm.trainer_id, businessWorkingHours, trainerSchedules]);

  // Edit request: Calculate valid time slots
  const getEditValidTimeSlots = () => {
    if (!editRequestForm.date || !editRequestForm.trainer_id) return [];
    
    const dayOfWeek = new Date(editRequestForm.date).getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    
    const timeToMins = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    const businessDay = businessWorkingHours[dayName];
    const businessOpen = businessDay?.open || "08:00";
    const businessClose = businessDay?.close || "22:00";
    const businessClosed = businessDay?.closed === true;
    
    if (businessClosed) return [];
    
    let workStart = timeToMins(businessOpen);
    let workEnd = timeToMins(businessClose);
    
    const trainerDaySchedules = trainerSchedules[editRequestForm.trainer_id] || [];
    const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
    
    if (trainerDaySchedules.length > 0 && !trainerDaySchedule) return [];
    
    if (trainerDaySchedule) {
      const trainerStart = timeToMins(trainerDaySchedule.start_time);
      const trainerEnd = timeToMins(trainerDaySchedule.end_time);
      workStart = Math.max(workStart, trainerStart);
      workEnd = Math.min(workEnd, trainerEnd);
    }
    
    const slots = [];
    for (let mins = workStart; mins < workEnd; mins += 30) {
      const hour = Math.floor(mins / 60);
      const minute = mins % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  };

  // Edit request: Update valid time slots
  useEffect(() => {
    if (showEditRequestModal) {
      const slots = getEditValidTimeSlots();
      setEditValidTimeSlots(slots);
    }
  }, [editRequestForm.date, editRequestForm.trainer_id, businessWorkingHours, trainerSchedules, showEditRequestModal]);

  // Edit request: Check room availability
  const checkEditRoomAvailability = async () => {
    if (!editRequestForm.room_id || !editRequestForm.date || !editRequestForm.start_time) return;
    try {
      let url = `${API}/rooms/${editRequestForm.room_id}/availability?date=${editRequestForm.date}&time=${editRequestForm.start_time}`;
      if (editRequestForm.end_time) url += `&end_time=${editRequestForm.end_time}`;
      if (editingRequest?.request_id) url += `&exclude_request_id=${editingRequest.request_id}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) setEditRoomAvailability(await response.json());
    } catch (error) {
      console.error("Error checking edit room availability:", error);
    }
  };

  // Edit request: Check trainer availability
  const checkEditTrainerAvailability = async () => {
    if (!editRequestForm.trainer_id || !editRequestForm.date || !editRequestForm.start_time) return;
    try {
      let url = `${API}/trainers/${editRequestForm.trainer_id}/availability?date=${editRequestForm.date}&time=${editRequestForm.start_time}`;
      if (editRequestForm.end_time) url += `&end_time=${editRequestForm.end_time}`;
      if (editingRequest?.request_id) url += `&exclude_request_id=${editingRequest.request_id}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) setEditTrainerAvailability(await response.json());
    } catch (error) {
      console.error("Error checking edit trainer availability:", error);
    }
  };

  // Edit request: Check member time conflict
  const checkEditMemberTimeConflict = () => {
    if (!editRequestForm.date || !editRequestForm.start_time || !editRequestForm.end_time) {
      setEditMemberTimeConflict(null);
      return;
    }
    
    const existingReservations = reservations.filter(r => 
      r.date === editRequestForm.date && 
      r.status !== "cancelled" && r.status !== "rejected"
    );
    
    const existingRequests = myRequests.filter(r => 
      r.date === editRequestForm.date && 
      (r.status === "pending" || r.status === "waiting") &&
      r.request_id !== editingRequest?.request_id
    );
    
    const [startH, startM] = editRequestForm.start_time.split(":").map(Number);
    const [endH, endM] = editRequestForm.end_time.split(":").map(Number);
    const newStartMinutes = startH * 60 + startM;
    const newEndMinutes = endH * 60 + endM;
    
    for (const res of existingReservations) {
      const [resStartH, resStartM] = (res.start_time || "00:00").split(":").map(Number);
      const [resEndH, resEndM] = (res.end_time || "23:59").split(":").map(Number);
      const resStartMinutes = resStartH * 60 + resStartM;
      const resEndMinutes = resEndH * 60 + resEndM;
      
      if (newStartMinutes < resEndMinutes && newEndMinutes > resStartMinutes) {
        setEditMemberTimeConflict({
          has_conflict: true,
          message: `Bu saatte zaten bir rezervasyonunuz var (${res.start_time}-${res.end_time})`
        });
        return;
      }
    }
    
    for (const req of existingRequests) {
      const [reqStartH, reqStartM] = (req.start_time || "00:00").split(":").map(Number);
      const [reqEndH, reqEndM] = (req.end_time || "23:59").split(":").map(Number);
      const reqStartMinutes = reqStartH * 60 + reqStartM;
      const reqEndMinutes = reqEndH * 60 + reqEndM;
      
      if (newStartMinutes < reqEndMinutes && newEndMinutes > reqStartMinutes) {
        setEditMemberTimeConflict({
          has_conflict: true,
          message: `Bu saatte zaten bir talebiniz var (${req.start_time}-${req.end_time})`
        });
        return;
      }
    }
    
    setEditMemberTimeConflict({ has_conflict: false });
  };

  // Edit request: Trigger availability checks
  useEffect(() => {
    if (showEditRequestModal && editRequestForm.room_id && editRequestForm.start_time && editRequestForm.date && editRequestForm.end_time) {
      checkEditRoomAvailability();
    }
  }, [editRequestForm.room_id, editRequestForm.start_time, editRequestForm.end_time, editRequestForm.date, showEditRequestModal]);

  useEffect(() => {
    if (showEditRequestModal && editRequestForm.trainer_id && editRequestForm.start_time && editRequestForm.date && editRequestForm.end_time) {
      checkEditTrainerAvailability();
    }
  }, [editRequestForm.trainer_id, editRequestForm.start_time, editRequestForm.end_time, editRequestForm.date, showEditRequestModal]);

  useEffect(() => {
    if (showEditRequestModal && editRequestForm.date && editRequestForm.start_time && editRequestForm.end_time) {
      checkEditMemberTimeConflict();
    }
  }, [editRequestForm.date, editRequestForm.start_time, editRequestForm.end_time, showEditRequestModal]);

  const handleCreateRequest = async () => {
    if (!requestForm.business_id || !requestForm.branch || !requestType || !requestForm.trainer_id || !requestForm.room_id || !requestForm.date || !requestForm.start_time) {
      toast.error(t("memberDashboard:fillAllRequired"));
      return;
    }
    
    if (requestType === "package" && !requestForm.member_package_id) {
      toast.error(t('packages:selectPackage'));
      return;
    }

    if (requestType === "card" && !requestForm.training_type) {
      toast.error(t("memberDashboard:selectTrainingType"));
      return;
    }
    
    // Check member time conflict
    if (memberTimeConflict && memberTimeConflict.has_conflict) {
      toast.error(memberTimeConflict.message || "Bu saatte zaten bir rezervasyonunuz var");
      return;
    }
    
    // Check availability before submitting
    if (trainerAvailability && !trainerAvailability.is_available) {
      toast.error(t('reservations:trainerNotAvailable'));
      return;
    }
    
    if (roomAvailability && roomAvailability.is_locked) {
      toast.error(t('reservations:roomLockedAtTime'));
      return;
    }
    
    // Check room capacity
    if (roomAvailability && roomAvailability.available_capacity <= 0) {
      toast.error(t("memberDashboard:roomCapacityFull"));
      return;
    }

    setSendingRequest(true);
    setPageLoading(true);
    try {
      // Get member_id from selected membership
      const selectedMembershipData = memberships.find(m => m.business_id === requestForm.business_id);
      
      // Determine payment type based on request type
      let paymentType = "package";
      if (requestType === "demo") {
        paymentType = "demo";
      } else if (requestType === "card") {
        paymentType = "card";
      }
      
      const submitData = {
        ...requestForm,
        member_id: selectedMembershipData?.member_id,
        is_demo: requestType === "demo",
        training_type: requestType === "demo" ? "demo" : requestForm.training_type,
        payment_type: paymentType
      };
      
      // Add card info if card payment
      if (requestType === "card") {
        submitData.card_type = selectedMembershipData?.member_card;
        delete submitData.member_package_id;
      }
      
      // If demo, remove package id
      if (requestType === "demo") {
        delete submitData.member_package_id;
      }
      
      // Use the new member-request endpoint which writes to reservations collection
      const response = await fetch(`${API}/reservations/member-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        toast.success(t("memberDashboard:reservationRequestSent"));
        setShowRequestModal(false);
        setRequestForm({
          business_id: "",
          trainer_id: "",
          room_id: "",
          branch: "",
          date: "",
          start_time: "",
          end_time: "",
          training_type: "one_on_one",
          member_package_id: "",
          notes: ""
        });
        setSelectedPackageInfo(null);
        setSelectedCard("");
        setRoomAvailability(null);
        setTrainerAvailability(null);
        setRequestType("");
        await fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:anErrorOccurred"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSendingRequest(false);
      setPageLoading(false);
    }
  };

  const handleChangeRequest = async () => {
    if (!selectedReservation || !changeForm.new_date || !changeForm.new_start_time || !changeForm.trainer_id || !changeForm.room_id) {
      toast.error(t('common:fillAllFields'));
      return;
    }

    // Check availability before submitting
    if (changeMemberTimeConflict && changeMemberTimeConflict.has_conflict) {
      toast.error(changeMemberTimeConflict.message || "Bu saatte zaten bir rezervasyonunuz var");
      return;
    }

    if (changeTrainerAvailability && !changeTrainerAvailability.is_available) {
      toast.error(t("memberDashboard:trainerNotAvailable"));
      return;
    }

    if (changeRoomAvailability && (changeRoomAvailability.is_locked || changeRoomAvailability.available_capacity <= 0)) {
      toast.error(t("memberDashboard:roomNotAvailable"));
      return;
    }

    setSendingChangeRequest(true);
    setPageLoading(true);
    try {
      const response = await fetch(`${API}/reservations/${selectedReservation.reservation_id}/member-change`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          date: changeForm.new_date,
          start_time: changeForm.new_start_time,
          end_time: changeForm.new_end_time,
          trainer_id: changeForm.trainer_id,
          room_id: changeForm.room_id,
          reason: changeForm.reason
        }),
      });

      if (response.ok) {
        toast.success(t("memberDashboard:changeRequestSent"));
        setShowChangeModal(false);
        setSelectedReservation(null);
        setChangeForm({ new_date: "", new_start_time: "", new_end_time: "", trainer_id: "", room_id: "", reason: "" });
        setChangeRoomAvailability(null);
        setChangeTrainerAvailability(null);
        setChangeMemberTimeConflict(null);
        setChangeValidTimeSlots([]);
        await fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:anErrorOccurred"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSendingChangeRequest(false);
      setPageLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Navigate to login page
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if API fails, clear local data and redirect
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      navigate("/login");
    }
  };

  // Open cancel modal and fetch cancellation policy
  const handleOpenCancelModal = async (reservation) => {
    setSelectedReservation(reservation);
    setCancelReason("");
    setCancellationPolicy(null);
    setShowCancelModal(true);
    
    // Fetch business cancellation policy
    try {
      const response = await fetch(`${API}/businesses/${reservation.business_id}/settings`, {
        credentials: "include"
      });
      if (response.ok) {
        const settings = await response.json();
        setCancellationPolicy({
          cancellation_window: settings.cancellation_window || 24,
          late_cancel_penalty: settings.late_cancel_penalty || false,
          late_cancel_action: settings.late_cancel_action || "burn_session"
        });
      }
    } catch (error) {
      console.error("Error fetching cancellation policy:", error);
    }
  };

  // Calculate if cancellation is late
  const isLateCancellation = () => {
    if (!selectedReservation || !cancellationPolicy) return false;
    
    const reservationDateTime = new Date(`${selectedReservation.date}T${selectedReservation.start_time || selectedReservation.time}`);
    const now = new Date();
    const hoursUntil = (reservationDateTime - now) / (1000 * 60 * 60);
    
    return hoursUntil < (cancellationPolicy.cancellation_window || 24);
  };

  // Handle reservation cancellation
  const handleCancelReservation = async () => {
    if (!selectedReservation) return;
    
    setCancelLoading(true);
    setPageLoading(true);
    try {
      const response = await fetch(`${API}/reservations/${selectedReservation.reservation_id}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          reason: cancelReason || t("memberDashboard:cancelledByMember"),
          force: false 
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.is_late_cancel) {
          toast.warning(t("memberDashboard:lateCancelWarning"), { duration: 5000 });
        } else {
          toast.success(t("memberDashboard:cancelledSuccessfully"));
        }
        
        setShowCancelModal(false);
        setSelectedReservation(null);
        setCancelReason("");
        fetchAllData(); // Refresh reservations
      } else {
        const error = await response.json();
        toast.error(error.detail || t("memberDashboard:cancelFailed"));
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setCancelLoading(false);
      setPageLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: t("memberDashboard:status.pending"), variant: "secondary", icon: AlertCircle },
      pending_member_approval: { label: t("memberDashboard:status.pendingMemberApproval"), variant: "secondary", icon: AlertCircle },
      pending_business_approval: { label: t("memberDashboard:status.pendingBusinessApproval"), variant: "secondary", icon: AlertCircle },
      change_pending_member: { label: t("memberDashboard:status.changePendingMember"), variant: "secondary", icon: AlertCircle },
      change_pending_business: { label: t("memberDashboard:status.changePendingBusiness"), variant: "secondary", icon: AlertCircle },
      confirmed: { label: t("memberDashboard:status.confirmed"), variant: "default", icon: CheckCircle },
      completed: { label: t("memberDashboard:status.completed"), variant: "success", icon: CheckCircle },
      cancelled: { label: t("memberDashboard:status.cancelled"), variant: "destructive", icon: XCircle },
      cancelled_late: { label: t("memberDashboard:status.cancelledLate"), variant: "destructive", icon: XCircle },
      rejected: { label: t("memberDashboard:status.rejected"), variant: "destructive", icon: XCircle },
      approved: { label: t("memberDashboard:status.approved"), variant: "default", icon: CheckCircle },
      burned: { label: t("memberDashboard:status.burned"), variant: "destructive", icon: XCircle },
    };
    const s = statusMap[status] || { label: status || t("memberDashboard:status.unknown"), variant: "secondary", icon: AlertCircle };
    return (
      <Badge variant={s.variant} className="flex items-center gap-1">
        <s.icon className="w-3 h-3" />
        {s.label}
      </Badge>
    );
  };

  const timeSlots = Array.from({ length: 14 }, (_, i) => {
    const hour = 8 + i;
    return `${hour.toString().padStart(2, "0")}:00`;
  });

  // Calculate stats
  const activePackages = packages.filter(p => p.is_active && p.remaining_sessions > 0);
  const totalRemainingSessions = activePackages.reduce((sum, p) => sum + (p.remaining_sessions || 0), 0);
  const upcomingReservations = reservations.filter(r => 
    r.status === "confirmed" && new Date(`${r.date} ${r.start_time || r.time}`) > new Date()
  );
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (memberships.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Building2 className="w-16 h-16 text-grey mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl text-navy mb-2">
                {t("memberDashboard:noMembershipYet")}
              </h2>
              <p className="text-grey mb-6">
                {t("memberDashboard:noMembershipDesc")}
              </p>
              <Button onClick={() => navigate("/")} className="bg-navy hover:bg-navy/90">
                {t("memberDashboard:homePage")}
              </Button>
            </CardContent>
          </Card>
          
          {/* Şifre Değiştir Kartı - Üyeliği olmayan kullanıcılar için */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {t("memberDashboard:passwordChangeTitle")}
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="rounded-xl"
                  data-testid="toggle-password-change-btn-no-membership"
                >
                  {showPasswordChange ? t("reservations:cancelledRes") : t("memberDashboard:changePassword")}
                </Button>
              </div>
            </CardHeader>
            {showPasswordChange && (
              <CardContent className="pt-2">
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                  {/* Current Password */}
                  <div className="space-y-2">
                    <Label>{t("common:currentPassword")}</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        placeholder={t("common:ph_mevcutSifreniziGirin")}
                        className="pr-10 rounded-xl"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* New Password */}
                  <div className="space-y-2">
                    <Label>{t("common:newPassword")}</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        placeholder={t("common:ph_yeniSifreniziGirinEnAz")}
                        className="pr-10 rounded-xl"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label>{t("memberDashboard:confirmNewPassword")}</Label>
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      placeholder={t("common:ph_yeniSifreniziTekrarGirin")}
                      className="rounded-xl"
                      autoComplete="new-password"
                    />
                  </div>
                  
                  {/* Change Button */}
                  <Button
                    onClick={handleChangePassword}
                    disabled={savingPassword}
                    className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                  >
                    {savingPassword ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        t("memberDashboard:changing")
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        {t("memberDashboard:changePasswordBtn")}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
          
          {/* Davet Kodu Kartı - Üyeliği olmayan kullanıcılar için */}
          <InvitationCodeCard />
        </div>
      </div>
    );
  }

  // Sidebar menu groups
  const sidebarMenuGroups = [
    {
      title: t("memberDashboard:sidebarGroup.general"),
      items: [
        { id: "home", label: t("memberDashboard:nav.home"), icon: Home },
        { id: "reservations", label: t("memberDashboard:nav.reservations"), icon: CalendarIcon, badge: (tabCounts.requests || 0) + (tabCounts.pending || 0) + (tabCounts.changes || 0) },
        { id: "packages", label: t("memberDashboard:nav.myPackages"), icon: Package },
        { id: "messages", label: t("memberDashboard:nav.messages"), icon: MessageCircle, badge: unreadMessageCount },
      ]
    },
    {
      title: t("memberDashboard:sidebarGroup.activities"),
      items: [
        { id: "events", label: t("memberDashboard:nav.events"), icon: CalendarDays, badge: eventNotificationCount },
        { id: "goals", label: t("memberDashboard:nav.goalsAndBadges"), icon: Target, badge: unseenBadgeCount },
        { id: "evaluations", label: t("memberDashboard:nav.evaluations"), icon: MessageSquare, badge: unreadEvaluationCount },
      ]
    },
    {
      title: t("memberDashboard:sidebarGroup.documents"),
      items: [
        { id: "forms", label: t("memberDashboard:nav.forms"), icon: FileText, badge: pendingFormCount },
        { id: "surveys", label: t("memberDashboard:nav.surveys"), icon: ClipboardList, badge: pendingSurveyCount },
      ]
    },
    {
      title: t("memberDashboard:sidebarGroup.account"),
      items: [
        { id: "history", label: t("memberDashboard:nav.history"), icon: History },
        { id: "profile", label: t("memberDashboard:nav.profile"), icon: User },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex relative">
      {/* Full Page Loading Overlay */}
      {pageLoading && (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 flex flex-col items-center gap-3 shadow-xl">
            <Loader2 className="w-10 h-10 animate-spin text-navy" />
            <p className="text-navy font-medium">{t("memberDashboard:processing")}</p>
          </div>
        </div>
      )}
      
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:w-64 md:flex-col bg-white border-r fixed inset-y-0 left-0 z-30">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b">
          <img 
            src="https://customer-assets.emergentagent.com/job_273f8795-754a-4865-8c8d-c4895c64dfec/artifacts/80de64xp_output-onlinepngtools.png" 
            alt="Probody Studio Logo" 
            className="w-10 h-10 object-contain"
          />
          <span className="font-heading font-bold text-navy ml-3">{t("common:memberPanel")}</span>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto sidebar-scroll">
          {sidebarMenuGroups.map((group, idx) => (
            <div key={group.title} className={idx > 0 ? "mt-6" : ""}>
              <h3 className="px-3 text-xs font-semibold text-grey uppercase tracking-wider mb-2">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      activeTab === item.id
                        ? "bg-navy text-white"
                        : "text-navy hover:bg-slate-100"
                    }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    {item.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${
                        activeTab === item.id
                          ? "bg-white text-navy"
                          : item.id === "forms" || item.id === "events" 
                            ? "bg-orange-500 text-white"
                            : "bg-red-500 text-white"
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        
        {/* User Info & Logout */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <Avatar>
              <AvatarFallback className="bg-navy text-white">
                {user?.name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy truncate">{user?.name} {user?.surname}</p>
              <p className="text-xs text-grey truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/?map=1")}
              className="flex-1 rounded-xl text-xs"
            >
              <Map className="w-3.5 h-3.5 mr-1" />
              {t('common:map')}
            </Button>
            <LanguageSwitcher variant="minimal" className="text-grey" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-xl text-xs text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`fixed inset-y-0 left-0 w-72 bg-white z-50 transform transition-transform md:hidden ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_273f8795-754a-4865-8c8d-c4895c64dfec/artifacts/80de64xp_output-onlinepngtools.png" 
              alt="Probody Studio Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-heading font-bold text-navy">{t("common:memberPanel")}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
            <XCircle className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          {sidebarMenuGroups.map((group, idx) => (
            <div key={group.title} className={idx > 0 ? "mt-6" : ""}>
              <h3 className="px-3 text-xs font-semibold text-grey uppercase tracking-wider mb-2">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      activeTab === item.id
                        ? "bg-navy text-white"
                        : "text-navy hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    {item.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${
                        activeTab === item.id
                          ? "bg-white text-navy"
                          : item.id === "forms" || item.id === "events" 
                            ? "bg-orange-500 text-white"
                            : "bg-red-500 text-white"
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        
        {/* User Info & Logout */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <Avatar>
              <AvatarFallback className="bg-navy text-white">
                {user?.name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy truncate">{user?.name}</p>
              <p className="text-xs text-grey truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/?map=1")}
              className="flex-1 rounded-xl text-xs"
            >
              <Map className="w-3.5 h-3.5 mr-1" />
              {t('common:map')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-xl text-xs text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-64">
        {/* Top Header - Mobile */}
        <header className="md:hidden bg-white border-b sticky top-0 z-40">
          <div className="px-4 py-3 flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-6 h-6 text-navy" />
            </button>
            <span className="font-heading font-bold text-navy">
              {sidebarMenuGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || t("memberDashboard:memberPanel")}
            </span>
            <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
              <SelectTrigger className="w-32 rounded-xl text-xs">
                <SelectValue placeholder={t("common:business")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:all')}</SelectItem>
                {memberships.map((m) => (
                  <SelectItem key={m.business_id} value={m.business_id}>
                    {m.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        {/* Top Header - Desktop */}
        <header className="hidden md:block bg-white border-b sticky top-0 z-20">
          <div className="px-6 py-4 flex items-center justify-between">
            <h1 className="font-heading font-bold text-xl text-navy">
              {sidebarMenuGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || t("memberDashboard:nav.home")}
            </h1>
            <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
              <SelectTrigger className="w-56 rounded-xl">
                <SelectValue placeholder={t("common:selectBusiness")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common:tumİsletmeler")}</SelectItem>
                {memberships.map((m) => (
                  <SelectItem key={m.business_id} value={m.business_id}>
                    {m.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 md:p-6">
          {/* Persistent Onboarding Banner - Show on ALL tabs if there are pending required forms */}
          {pendingFormCount > 0 && (
            <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <AlertCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">
                        <strong>{pendingFormCount} {t("memberDashboard:pendingFormsCount")}</strong> {t("memberDashboard:mustFillForms")}
                      </p>
                      <p className="text-sm text-white/80">
                        {t("memberDashboard:formsImportant")}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab("forms")}
                    className="bg-white text-orange-600 hover:bg-white/90 rounded-xl font-semibold px-4 py-2"
                    size="sm"
                    data-testid="onboarding-banner-btn"
                  >
                    {t("memberDashboard:fillForms")}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* HOME TAB */}
          {activeTab === "home" && (
            <div className="space-y-6">

            {/* Pending Change Requests Banner */}
            {tabCounts.changes > 0 && (
              <div
                className="p-4 rounded-2xl bg-gradient-to-r from-purple-100 to-purple-50 border border-purple-300 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => { setActiveTab("reservations"); setReservationSubTab("changes"); }}
                data-testid="pending-changes-banner"
              >
                <div className="flex items-center gap-3 justify-between flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-200 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-purple-700" />
                    </div>
                    <div>
                      <p className="font-bold text-purple-900">
                        {tabCounts.changes} adet bekleyen rezervasyon değişiklik talebi
                      </p>
                      <p className="text-xs text-purple-700">
                        İşletmeniz rezervasyon detaylarını güncellemek istiyor. Lütfen yanıt verin.
                      </p>
                    </div>
                  </div>
                  <Button
                    className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={(e) => { e.stopPropagation(); setActiveTab("reservations"); setReservationSubTab("changes"); }}
                    data-testid="pending-changes-cta"
                  >
                    Şimdi İncele
                  </Button>
                </div>
              </div>
            )}

            {/* Pending Requests Banner (from member to business) */}
            {tabCounts.requests > 0 && (
              <div
                className="p-3 rounded-xl bg-amber-50 border border-amber-200 cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => { setActiveTab("reservations"); setReservationSubTab("requests"); }}
                data-testid="pending-requests-banner"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <p className="text-sm font-medium text-amber-800">
                    {tabCounts.requests} adet işletmenizden gelen rezervasyon talebi var. Yanıtlayın →
                  </p>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy">{activePackages.length}</p>
                      <p className="text-xs text-grey">{t("members:activePackage")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy">{totalRemainingSessions}</p>
                      <p className="text-xs text-grey">{t("members:remainingSessions")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                      <CalendarIcon className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy">{upcomingReservations.length}</p>
                      <p className="text-xs text-grey">{t("common:yaklasanDers")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy">{trainingHistory.length}</p>
                      <p className="text-xs text-grey">{t("memberDashboard:completedStat")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{t("common:hizliİslemler")}</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-3 flex-wrap">
                {/* Online booking açıksa butonu göster */}
                {(businessSettings?.display_settings?.allow_online_booking !== false) && (
                  <Button 
                    className="bg-navy hover:bg-navy/90 rounded-xl"
                    onClick={() => {
                      if (memberships.length === 1) {
                        setRequestForm(prev => ({ ...prev, business_id: memberships[0].business_id }));
                        fetchBusinessData(memberships[0].business_id);
                      }
                      setRequestType(""); // Reset request type
                      setSelectedPackageInfo(null);
                      setShowRequestModal(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("memberDashboard:reservationRequest")}
                  </Button>
                )}
                {/* Online booking kapalıysa uyarı göster */}
                {(businessSettings?.display_settings?.allow_online_booking === false) && (
                  <div className="text-sm text-amber-600 bg-amber-50 px-4 py-2 rounded-xl flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {t("memberDashboard:onlineBookingDisabled")}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Reservations */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{t("common:yaklasanDersler")}</CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingReservations.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingReservations.slice(0, 5).map((res) => (
                      <div 
                        key={res.reservation_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-navy/10 rounded-xl flex flex-col items-center justify-center">
                            <span className="text-xs text-grey">
                              {res.date ? format(new Date(res.date), "MMM", { locale: getDateLocale() }) : ""}
                            </span>
                            <span className="font-bold text-navy">
                              {res.date ? format(new Date(res.date), "d") : ""}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-navy">{getLocalizedBranch(res.branch)}</p>
                            <p className="text-sm text-grey">
                              {res.start_time || res.time} • {res.trainer_name || t("memberDashboard:trainerRole")}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(res.status)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{t("common:yaklasanDersBulunmuyor")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* PACKAGES TAB */}
          {activeTab === "packages" && (
            <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-xl text-navy">{t("memberDashboard:myPackagesTitle")}</h2>
            </div>

            {packages.length > 0 ? (
              <div className="grid gap-4">
                {packages.map((pkg) => {
                  // is_active undefined ise status'a bak
                  const isPackageActive = pkg.is_active !== false && (pkg.status === 'active' || pkg.status === undefined);
                  const isActive = isPackageActive && pkg.remaining_sessions > 0;
                  const isExpired = pkg.end_date && new Date(pkg.end_date) < new Date();
                  const isPaid = pkg.payment_status === "paid" || pkg.is_paid;
                  const paidAmount = pkg.paid_amount || 0;
                  const totalPrice = pkg.price || pkg.sale_price || pkg.total_price || 0;
                  const debt = totalPrice - paidAmount;
                  
                  return (
                    <Card key={pkg.member_package_id} className="border-0 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              {pkg.package_no && (
                                <span className="font-mono text-xs text-grey-600 bg-slate-50 border border-grey-200 px-1.5 py-0.5 rounded">
                                  {pkg.package_no}
                                </span>
                              )}
                              <h3 className="font-semibold text-navy">{pkg.package_name}</h3>
                              {pkg.status === "cancelled" ? (
                                <Badge className="bg-red-100 text-red-700">
                                  {pkg.cancel_type === "refund" ? t("memberDashboard:refundCancel") : t("memberDashboard:freeCancel")}
                                </Badge>
                              ) : isActive ? (
                                <Badge className="bg-green-100 text-green-700">{t('admin:active')}</Badge>
                              ) : !isPackageActive ? (
                                <Badge variant="secondary">{t('common:passive')}</Badge>
                              ) : null}
                              {debt > 0 && pkg.status !== "cancelled" && (
                                <Badge className="bg-red-100 text-red-700">{currencySymbol}{debt.toLocaleString('tr-TR')} {t("memberDashboard:debt")}</Badge>
                              )}
                              {isExpired && isPackageActive && pkg.status !== "cancelled" && (
                                <Badge className="bg-red-100 text-red-700">{t('members:expired')}</Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-grey mb-3">
                              <span>{pkg.business_name}</span>
                              <span>•</span>
                              <span>{getLocalizedBranch(pkg.package_branch || pkg.branch) || "-"}</span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-grey">{t("memberDashboard:remainingTotal")}</p>
                                <p className="font-semibold text-navy">
                                  {pkg.remaining_sessions} / {pkg.total_sessions}
                                </p>
                              </div>
                              <div>
                                <p className="text-grey">{t('common:price')}</p>
                                <p className="font-semibold text-navy">{currencySymbol}{totalPrice.toLocaleString('tr-TR')}</p>
                              </div>
                              <div>
                                <p className="text-grey">{t('settings:startTime')}</p>
                                <p className="font-semibold text-navy">
                                  {pkg.start_date ? format(new Date(pkg.start_date), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                                </p>
                              </div>
                              <div>
                                <p className="text-grey">{t('settings:endTime')}</p>
                                <p className="font-semibold text-navy">
                                  {pkg.end_date ? format(new Date(pkg.end_date), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                                </p>
                              </div>
                            </div>
                            
                            {/* Ödeme Durumu - İşletme paneli gibi detaylı */}
                            <div className="mt-4 pt-4 border-t">
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-grey">{t("memberDashboard:paymentStatus")}</p>
                                <p className="text-sm font-medium">
                                  {paidAmount > 0 ? (
                                    <span className="text-green-600">
                                      {currencySymbol}{paidAmount.toLocaleString('tr-TR')} {t("memberDashboard:paid")}
                                      {debt > 0 && (
                                        <span className="text-red-600 ml-1">
                                          ({currencySymbol}{debt.toLocaleString('tr-TR')} {t("memberDashboard:remaining")})
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-orange-600">{t("common:odemeYapilmadi")}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            
                            {/* Ödeme Geçmişi - Her paket için ayrı */}
                            {pkg.payment_history && pkg.payment_history.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-sm font-medium text-navy mb-2">{t('payments:paymentHistory')}</p>
                                <div className="space-y-2">
                                  {pkg.payment_history.map((payment, idx) => (
                                    <div key={payment.payment_id || idx} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg p-2">
                                      <div>
                                        <p className="text-grey">
                                          {payment.created_at ? format(new Date(payment.created_at), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                                        </p>
                                        {payment.notes && (
                                          <p className="text-xs text-slate-500">{payment.notes}</p>
                                        )}
                                      </div>
                                      <p className={`font-medium ${payment.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {payment.amount >= 0 ? '+' : ''}{currencySymbol}{Math.abs(payment.amount || 0).toLocaleString('tr-TR')}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-grey mx-auto mb-3" />
                  <p className="text-grey">{t("packages:noPackagesYet")}</p>
                </CardContent>
              </Card>
            )}

            {/* Payment History */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t("memberDashboard:paymentHistory")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {payments.length > 0 ? (
                  <div className="space-y-3">
                    {payments.slice(0, 10).map((payment) => {
                      // From member's perspective:
                      // Positive amount (payment) = money they gave = RED (expense)
                      // Negative amount (refund) = money they got back = GREEN (income)
                      const amount = payment.amount || 0;
                      const isRefund = amount < 0;
                      const displayAmount = Math.abs(amount);
                      
                      return (
                        <div 
                          key={payment.payment_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                        >
                          <div>
                            <p className="font-medium text-navy flex items-center gap-2 flex-wrap">
                              {payment.package_no && (
                                <span className="font-mono text-xs text-grey-600 bg-white border border-grey-200 px-1.5 py-0.5 rounded">
                                  {payment.package_no}
                                </span>
                              )}
                              <span>{payment.package_name}</span>
                              {isRefund && <span className="text-xs text-grey">{t("memberDashboard:refund")}</span>}
                            </p>
                            <p className="text-sm text-grey">
                              {payment.payment_date ? format(new Date(payment.payment_date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}
                            </p>
                          </div>
                          <span className={`font-semibold ${isRefund ? 'text-green-600' : 'text-red-600'}`}>
                            {isRefund ? '+' : '-'}{formatPrice(displayAmount)}
                          </span>
                        </div>
                      );
                    })}
                    
                    {/* Total Summary */}
                    <div className="border-t pt-3 mt-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-grey">{t("memberDashboard:totalSpent")}</span>
                        <span className="font-bold text-navy">
                          {formatPrice(payments.reduce((sum, p) => sum + (p.amount > 0 ? p.amount : 0), 0))}
                        </span>
                      </div>
                      {payments.some(p => p.amount < 0) && (
                        <div className="flex items-center justify-between text-sm mt-1">
                          <span className="text-grey">{t("memberDashboard:totalRefund")}</span>
                          <span className="font-bold text-green-600">
                            +{formatPrice(Math.abs(payments.reduce((sum, p) => sum + (p.amount < 0 ? p.amount : 0), 0)))}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t">
                        <span className="font-medium">{t("memberDashboard:netSpending")}</span>
                        <span className={`font-bold ${payments.reduce((sum, p) => sum + p.amount, 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {payments.reduce((sum, p) => sum + p.amount, 0) > 0 ? '-' : '+'}{formatPrice(Math.abs(payments.reduce((sum, p) => sum + p.amount, 0)))}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-grey py-4">{t("common:odemeKaydiYok")}</p>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* RESERVATIONS TAB - YENİ 4-TAB SİSTEMİ */}
          {activeTab === "reservations" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl text-navy">{t('common:reservations')}</h2>
                {/* Online booking açıksa butonu göster */}
                {(businessSettings?.display_settings?.allow_online_booking !== false) ? (
                  <Button 
                    className="bg-navy hover:bg-navy/90 rounded-xl"
                    onClick={() => {
                      // Reset form completely
                      setRequestForm({
                        business_id: memberships.length === 1 ? memberships[0].business_id : "",
                        trainer_id: "",
                        room_id: "",
                        branch: "",
                        date: "",
                        start_time: "",
                        end_time: "",
                        training_type: "one_on_one",
                        member_package_id: "",
                        notes: ""
                      });
                      if (memberships.length === 1) {
                        fetchBusinessData(memberships[0].business_id);
                      }
                      setRequestType("");
                      setSelectedPackageInfo(null);
                      setRoomAvailability(null);
                      setTrainerAvailability(null);
                      setMemberTimeConflict(null);
                      setFilteredTrainersForRequest([]);
                      setShowRequestModal(true);
                    }}
                    data-testid="new-request-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Yeni Talep
                  </Button>
                ) : (
                  <div className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {t("memberDashboard:onlineResDisabled")}
                  </div>
                )}
              </div>

              {/* NEW 4-TAB SYSTEM */}
              <Tabs value={reservationSubTab} onValueChange={setReservationSubTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger value="reservations" className="rounded-xl text-xs sm:text-sm" data-testid="tab-reservations">
                    <CalendarIcon className="w-3 h-3 mr-1 hidden sm:inline" />
                    {t("memberDashboard:reservationsTab")}
                  </TabsTrigger>
                  <TabsTrigger value="requests" className="rounded-xl relative text-xs sm:text-sm data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-requests">
                    <ClipboardList className="w-3 h-3 mr-1 hidden sm:inline" />
                    {t("memberDashboard:requestsTab")}
                    {tabCounts.requests > 0 && (
                      <span className="ml-1 bg-amber-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                        {tabCounts.requests}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-green-500 data-[state=active]:text-white" data-testid="tab-pending">
                    <Clock className="w-3 h-3 mr-1 hidden sm:inline" />
                    {t("memberDashboard:pendingTab")}
                    {tabCounts.pending > 0 && (
                      <span className="ml-1 bg-green-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                        {tabCounts.pending}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="changes" className="rounded-xl relative text-xs sm:text-sm data-[state=active]:bg-purple-500 data-[state=active]:text-white" data-testid="tab-changes">
                    <Edit className="w-3 h-3 mr-1 hidden sm:inline" />
                    {t("memberDashboard:changesTab")}
                    {tabCounts.changes > 0 && (
                      <span className="ml-1 bg-purple-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                        {tabCounts.changes}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* TAB 1: Rezervasyonlar (approved, cancelled, completed) */}
                <TabsContent value="reservations">
                  {(() => {
                    // Filter reservations for this tab only - kesinleşmiş durumlar
                    const tabReservations = reservations.filter(r => 
                      ["approved", "confirmed", "cancelled", "cancelled_late", "completed", "burned"].includes(r.status)
                    );
                    
                    return tabReservations.length > 0 ? (
                    <div className="space-y-3">
                      {tabReservations.map((res) => (
                        <Card key={res.reservation_id} className="border-0 shadow-sm" data-testid={`reservation-card-${res.reservation_id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                                  res.status === "cancelled" || res.status === "cancelled_late" ? "bg-red-50" :
                                  res.status === "completed" ? "bg-green-50" :
                                  "bg-navy/5"
                                }`}>
                                  <span className="text-xs text-grey">
                                    {res.date ? format(new Date(res.date), "MMM", { locale: getDateLocale() }) : ""}
                                  </span>
                                  <span className="font-bold text-navy text-lg">
                                    {res.date ? format(new Date(res.date), "d") : ""}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-navy">{getLocalizedBranch(res.branch)}</p>
                                    {getStatusBadge(res.status)}
                                  </div>
                                  <p className="text-sm text-grey">
                                    {res.start_time || res.time} - {res.end_time || ""} • {res.trainer_name}
                                  </p>
                                  <p className="text-xs text-grey">{res.business_name}</p>
                                </div>
                              </div>
                              
                              {/* Actions for confirmed/approved reservations */}
                              {(res.status === "confirmed" || res.status === "approved") && (
                                <div className="flex gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                                    disabled={actionLoading !== null}
                                    onClick={() => {
                                      setViewingReservation(res);
                                      setShowViewModal(true);
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="rounded-xl"
                                    disabled={actionLoading !== null}
                                    onClick={async () => {
                                      setActionLoading(`change-res-${res.reservation_id}`);
                                      setSelectedReservation(res);
                                      // Mevcut rezervasyon verileriyle formu doldur
                                      setChangeForm({
                                        new_date: res.date || "",
                                        new_start_time: res.start_time || res.time || "",
                                        new_end_time: res.end_time || "",
                                        trainer_id: res.trainer_id || "",
                                        room_id: res.room_id || "",
                                        reason: ""
                                      });
                                      if (res.business_id) {
                                        setLoadingBusinessData(true);
                                        await fetchBusinessData(res.business_id);
                                      }
                                      setShowChangeModal(true);
                                      setActionLoading(null);
                                    }}
                                    data-testid={`change-btn-${res.reservation_id}`}
                                  >
                                    {actionLoading === `change-res-${res.reservation_id}` ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <Edit className="w-4 h-4 mr-1" />
                                    )}
                                    {actionLoading === `change-res-${res.reservation_id}` ? t("common:loadingDots") : t("common:change")}
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                                    disabled={actionLoading !== null}
                                    onClick={() => handleOpenCancelModal(res)}
                                    data-testid={`cancel-btn-${res.reservation_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    {t("memberDashboard:cancelBtn")}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <CalendarIcon className="w-12 h-12 text-grey mx-auto mb-3" />
                        <p className="text-grey">{t("common:henuzRezervasyonYok")}</p>
                      </CardContent>
                    </Card>
                  );
                  })()}
                </TabsContent>

                {/* TAB 2: Talepler - İşletmeden gelen yeni talepler (pending_member_approval) */}
                <TabsContent value="requests">
                  {incomingRequests.length > 0 ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-amber-50 rounded-xl mb-4">
                        <p className="text-sm text-amber-700 font-medium">
                          ⚠️ {t("memberDashboard:businessCreatedRes")}
                        </p>
                      </div>
                      {incomingRequests.map((req) => {
                        const isExternalBusiness = req.business_id && !memberships.some(m => m.business_id === req.business_id);
                        return (
                        <Card key={req.reservation_id} className={`border-0 shadow-sm border-l-4 ${isExternalBusiness ? 'border-l-purple-500' : 'border-l-amber-400'}`} data-testid={`incoming-request-${req.reservation_id}`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center gap-4">
                                <div className="w-20 h-14 bg-amber-50 rounded-xl flex flex-col items-center justify-center">
                                  <span className="text-xs text-grey">
                                    {req.date ? format(new Date(req.date), "dd.MM.yyyy", { locale: getDateLocale() }) : ""}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-navy">{getLocalizedBranch(req.branch)}</p>
                                    <Badge className="bg-amber-100 text-amber-700">{t('admin:pendingApproval')}</Badge>
                                    {isExternalBusiness && (
                                      <Badge
                                        className="bg-purple-100 text-purple-700 border-purple-300"
                                        data-testid={`external-business-badge-${req.reservation_id}`}
                                        title={t("memberDashboard:externalBusinessInfo")}
                                      >
                                        {t("memberDashboard:externalBusinessBadge")}
                                      </Badge>
                                    )}
                                    {req.is_demo && <Badge variant="outline" className="text-green-600 border-green-300">Demo</Badge>}
                                  </div>
                                  <p className="text-sm text-grey">
                                    {req.start_time} - {req.end_time || ""} • {req.trainer_name || t("memberDashboard:trainerRole")}
                                  </p>
                                  <p className={`text-xs ${isExternalBusiness ? 'text-purple-700 font-semibold' : 'text-grey'}`}>
                                    {req.business_name || t("memberDashboard:businessLabel")}
                                  </p>
                                  {req.package_name && (
                                    <p className="text-xs text-blue-600">{t("packages:package")}: {req.package_name}</p>
                                  )}
                                  {req.room_name && (
                                    <p className="text-xs text-grey">{t("common:room")}: {req.room_name}</p>
                                  )}
                                  {req.notes && (
                                    <p className="text-xs text-blue-600 mt-1">{t("common:note")}: {req.notes}</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Detay butonu ve action buttons */}
                              <div className="flex gap-2 justify-between items-center">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="rounded-xl text-navy"
                                  onClick={() => {
                                    setSelectedReservationDetail(req);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Detay
                                </Button>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    className="rounded-xl bg-green-600 hover:bg-green-700"
                                    disabled={actionLoading !== null}
                                    onClick={async () => {
                                      setActionLoading(`approve-${req.reservation_id}`);
                                      try {
                                        const response = await fetch(`${API}/reservations/${req.reservation_id}/member-approve`, {
                                          method: "PUT",
                                          credentials: "include",
                                        });
                                        if (response.ok) {
                                          toast.success(t('reservations:reservationApprovedSuccess'));
                                          fetchAllData();
                                        } else {
                                          const error = await response.json();
                                          toast.error(error.detail || t("memberDashboard:approveFailed"));
                                        }
                                      } catch (error) {
                                        toast.error(t('common:errorOccurred'));
                                      } finally {
                                        setActionLoading(null);
                                      }
                                    }}
                                    data-testid={`approve-btn-${req.reservation_id}`}
                                  >
                                    {actionLoading === `approve-${req.reservation_id}` ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="w-4 h-4 mr-1" />
                                    )}
                                    {actionLoading === `approve-${req.reservation_id}` ? t("memberDashboard:approving") : t("admin:confirm")}
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                                    disabled={actionLoading !== null}
                                    onClick={async () => {
                                      setActionLoading(`change-${req.reservation_id}`);
                                      // Mevcut seçimleri koru
                                      setSelectedReservation(req);
                                      setChangeForm({
                                        new_date: req.date || "",
                                        new_start_time: req.start_time || req.time || "",
                                        new_end_time: req.end_time || "",
                                        trainer_id: req.trainer_id || "",
                                        room_id: req.room_id || "",
                                        reason: ""
                                      });
                                      if (req.business_id) {
                                        setLoadingBusinessData(true);
                                        await fetchBusinessData(req.business_id);
                                      }
                                      setShowChangeModal(true);
                                      setActionLoading(null);
                                    }}
                                    data-testid={`change-btn-${req.reservation_id}`}
                                  >
                                    {actionLoading === `change-${req.reservation_id}` ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <Edit className="w-4 h-4 mr-1" />
                                    )}
                                    {actionLoading === `change-${req.reservation_id}` ? t("common:loadingDots") : t("common:change")}
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                                    disabled={actionLoading !== null}
                                    onClick={async () => {
                                      setActionLoading(`reject-${req.reservation_id}`);
                                      try {
                                        const response = await fetch(`${API}/reservations/${req.reservation_id}/member-reject`, {
                                          method: "PUT",
                                          headers: { "Content-Type": "application/json" },
                                          credentials: "include",
                                          body: JSON.stringify({ reason: t("memberDashboard:rejectedByMember") })
                                        });
                                        if (response.ok) {
                                          toast.success(t('common:requestRejected'));
                                          fetchAllData();
                                        } else {
                                          const error = await response.json();
                                          toast.error(error.detail || t("memberDashboard:rejectFailed"));
                                        }
                                      } catch (error) {
                                        toast.error(t('common:errorOccurred'));
                                      } finally {
                                        setActionLoading(null);
                                      }
                                    }}
                                    data-testid={`reject-btn-${req.reservation_id}`}
                                  >
                                    {actionLoading === `reject-${req.reservation_id}` ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <XCircle className="w-4 h-4 mr-1" />
                                    )}
                                    {actionLoading === `reject-${req.reservation_id}` ? t("memberDashboard:rejecting") : t("memberDashboard:reject")}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <ClipboardList className="w-12 h-12 text-grey mx-auto mb-3" />
                        <p className="text-grey">{t("common:noRequestsFromBusiness")}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* TAB 3: Beklemede - Üyenin gönderdiği, işletme onayı bekleyen */}
                <TabsContent value="pending">
                  {pendingReservations.length > 0 ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded-xl mb-4">
                        <p className="text-sm text-blue-700 font-medium">
                          ⏳ {t("memberDashboard:pendingRequestsInfo")}
                        </p>
                      </div>
                      {pendingReservations.map((req) => (
                        <Card key={req.reservation_id} className="border-0 shadow-sm border-l-4 border-l-blue-400" data-testid={`pending-request-${req.reservation_id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-20 h-14 bg-blue-50 rounded-xl flex flex-col items-center justify-center">
                                  <span className="text-xs text-grey">
                                    {req.date ? format(new Date(req.date), "dd.MM.yyyy", { locale: getDateLocale() }) : ""}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-navy">{getLocalizedBranch(req.branch)}</p>
                                    <Badge className="bg-blue-100 text-blue-700">{t("memberDashboard:businessApprovalPending")}</Badge>
                                    {req.is_demo && <Badge variant="outline" className="text-green-600 border-green-300">Demo</Badge>}
                                  </div>
                                  <p className="text-sm text-grey">
                                    {req.start_time} - {req.end_time || ""} • {req.trainer_name || t("memberDashboard:trainerRole")}
                                  </p>
                                  <p className="text-xs text-grey">{req.business_name}</p>
                                </div>
                              </div>
                              
                              {/* View, Edit & Cancel buttons */}
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                                  disabled={actionLoading !== null}
                                  onClick={() => {
                                    setViewingReservation(req);
                                    setShowViewModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                                  disabled={actionLoading !== null}
                                  onClick={async () => {
                                    setActionLoading(`edit-pending-${req.reservation_id}`);
                                    // Mevcut seçimleri koru
                                    setSelectedReservation(req);
                                    setChangeForm({
                                      new_date: req.date || "",
                                      new_start_time: req.start_time || req.time || "",
                                      new_end_time: req.end_time || "",
                                      trainer_id: req.trainer_id || "",
                                      room_id: req.room_id || "",
                                      reason: ""
                                    });
                                    if (req.business_id) {
                                      setLoadingBusinessData(true);
                                      await fetchBusinessData(req.business_id);
                                    }
                                    setShowChangeModal(true);
                                    setActionLoading(null);
                                  }}
                                  data-testid={`edit-pending-${req.reservation_id}`}
                                >
                                  {actionLoading === `edit-pending-${req.reservation_id}` ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Edit className="w-4 h-4 mr-1" />
                                  )}
                                  {actionLoading === `edit-pending-${req.reservation_id}` ? t("common:loadingDots") : t("common:change")}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                                  disabled={actionLoading !== null}
                                  onClick={() => {
                                    setCancellingRequest(req);
                                    setCancelRequestReason("");
                                    setShowCancelRequestModal(true);
                                  }}
                                  data-testid={`cancel-pending-${req.reservation_id}`}
                                >
                                  {t("memberDashboard:cancelIt")}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <Clock className="w-12 h-12 text-grey mx-auto mb-3" />
                        <p className="text-grey">{t("memberDashboard:noPendingRequests")}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* TAB 4: Değişiklikler - İşletmeden gelen değişiklik talepleri */}
                <TabsContent value="changes">
                  {incomingChanges.length > 0 ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-purple-50 rounded-xl mb-4">
                        <p className="text-sm text-purple-700 font-medium">
                          🔄 {t("memberDashboard:businessChangeRequest")}
                        </p>
                      </div>
                      {incomingChanges.map((change) => (
                        <Card key={change.reservation_id} className="border-0 shadow-sm border-l-4 border-l-purple-400" data-testid={`change-request-${change.reservation_id}`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center gap-4">
                                <div className="w-20 h-14 bg-purple-50 rounded-xl flex flex-col items-center justify-center">
                                  <span className="text-xs text-grey">
                                    {change.date ? format(new Date(change.date), "dd.MM.yyyy", { locale: getDateLocale() }) : ""}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-navy">{getLocalizedBranch(change.branch)}</p>
                                    <Badge className="bg-purple-100 text-purple-700">{t("memberDashboard:changeRequest")}</Badge>
                                  </div>
                                  <p className="text-sm text-grey">
                                    {change.start_time} - {change.end_time || ""} • {change.trainer_name || t("memberDashboard:trainerRole")}
                                  </p>
                                  <p className="text-xs text-grey">{change.business_name}</p>
                                  {change.change_reason && (
                                    <p className="text-xs text-purple-600 mt-1">Neden: {change.change_reason}</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action buttons */}
                              <div className="flex gap-2 justify-end flex-wrap">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                                  onClick={() => {
                                    setViewingReservation(change);
                                    setShowViewModal(true);
                                  }}
                                  disabled={actionLoading !== null}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="rounded-xl bg-green-600 hover:bg-green-700"
                                  disabled={actionLoading !== null}
                                  onClick={async () => {
                                    setActionLoading(`approve-${change.reservation_id}`);
                                    try {
                                      const response = await fetch(`${API}/reservations/${change.reservation_id}/member-approve`, {
                                        method: "PUT",
                                        credentials: "include",
                                      });
                                      if (response.ok) {
                                        toast.success(t("admin:toast_degisiklikOnaylandi_75c5"));
                                        fetchAllData();
                                      } else {
                                        const error = await response.json();
                                        toast.error(error.detail || t("memberDashboard:approveFailed"));
                                      }
                                    } catch (error) {
                                      toast.error(t('common:errorOccurred'));
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                  data-testid={`approve-change-${change.reservation_id}`}
                                >
                                  {actionLoading === `approve-${change.reservation_id}` ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                  )}
                                  {actionLoading === `approve-${change.reservation_id}` ? t("memberDashboard:approving") : t("admin:confirm")}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                                  disabled={actionLoading !== null}
                                  onClick={async () => {
                                    setActionLoading(`change-${change.reservation_id}`);
                                    // Mevcut seçimleri koru
                                    setSelectedReservation(change);
                                    setChangeForm({
                                      new_date: change.date || "",
                                      new_start_time: change.start_time || change.time || "",
                                      new_end_time: change.end_time || "",
                                      trainer_id: change.trainer_id || "",
                                      room_id: change.room_id || "",
                                      reason: ""
                                    });
                                    if (change.business_id) {
                                      setLoadingBusinessData(true);
                                      await fetchBusinessData(change.business_id);
                                    }
                                    setShowChangeModal(true);
                                    setActionLoading(null);
                                  }}
                                  data-testid={`edit-change-${change.reservation_id}`}
                                >
                                  {actionLoading === `change-${change.reservation_id}` ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Edit className="w-4 h-4 mr-1" />
                                  )}
                                  {actionLoading === `change-${change.reservation_id}` ? t("common:loadingDots") : t("common:change")}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                                  disabled={actionLoading !== null}
                                  onClick={async () => {
                                    setActionLoading(`reject-${change.reservation_id}`);
                                    try {
                                      const response = await fetch(`${API}/reservations/${change.reservation_id}/member-reject`, {
                                        method: "PUT",
                                        headers: { "Content-Type": "application/json" },
                                        credentials: "include",
                                        body: JSON.stringify({ reason: t("memberDashboard:rejectedByMember") })
                                      });
                                      if (response.ok) {
                                        toast.success(t("admin:toast_degisiklikReddedildi"));
                                        fetchAllData();
                                      } else {
                                        const error = await response.json();
                                        toast.error(error.detail || t("memberDashboard:rejectFailed"));
                                      }
                                    } catch (error) {
                                      toast.error(t('common:errorOccurred'));
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                  data-testid={`reject-change-${change.reservation_id}`}
                                >
                                  {actionLoading === `reject-${change.reservation_id}` ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4 mr-1" />
                                  )}
                                  {actionLoading === `reject-${change.reservation_id}` ? t("memberDashboard:rejecting") : t("memberDashboard:reject")}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <Edit className="w-12 h-12 text-grey mx-auto mb-3" />
                        <p className="text-grey">{t("common:degisiklikTalebiYok")}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* FORMS TAB - Member Forms */}
          {activeTab === "forms" && (
            <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-xl text-navy">{t("memberDashboard:requiredForms")}</h2>
                <p className="text-sm text-grey">{t("surveys:doldurmanizGerekenVeDoldurdugunuzFormlar")}</p>
              </div>
            </div>

            {formsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : (
              <Tabs defaultValue="pending" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="pending" className="rounded-xl">
                    {t("memberDashboard:pendingCount")} ({pendingForms.length})
                  </TabsTrigger>
                  <TabsTrigger value="completed" className="rounded-xl">
                    {t("memberDashboard:completedCount")} ({completedForms.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pending">
                  {pendingForms.length > 0 ? (
                    <div className="space-y-3">
                      {pendingForms.map((form) => {
                        const IconComp = FORM_ICONS[form.icon] || FileText;
                        return (
                          <Card key={form.form_id} className="border-0 shadow-sm">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                    <IconComp className="w-6 h-6 text-orange-600" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <h3 className="font-semibold text-navy">{form.title}</h3>
                                      {form.is_required && (
                                        <Badge className="bg-red-100 text-red-700 text-xs">{t("memberDashboard:required")}</Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-grey">{form.description}</p>
                                    <p className="text-xs text-grey mt-1">{form.question_count} {t("memberDashboard:questions")}</p>
                                  </div>
                                </div>
                                <Button
                                  onClick={() => handleSelectForm(form)}
                                  className="bg-navy hover:bg-navy/90 rounded-xl"
                                  data-testid={`fill-form-${form.form_id}`}
                                >
                                  {t("memberDashboard:fill")}
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <p className="text-grey">{t("memberDashboard:allFormsFilled")}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="completed">
                  {completedForms.length > 0 ? (
                    <div className="space-y-3">
                      {completedForms.map((form) => (
                        <Card key={form.response_id} className="border-0 shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-navy">{form.title}</h3>
                                  <p className="text-sm text-grey">
                                    {form.completed_at && format(new Date(form.completed_at), "d MMMM yyyy HH:mm", { locale: getDateLocale() })}
                                    {form.edit_history?.length > 0 && (
                                      <span className="ml-2 text-amber-600">
                                        ({form.edit_history.length} {t("memberDashboard:editCount")})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => handleViewCompletedForm(form)}
                                  className="rounded-xl"
                                  data-testid={`view-form-${form.form_id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  {t("memberDashboard:view")}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-12 text-center">
                        <FileText className="w-12 h-12 text-grey mx-auto mb-3 opacity-50" />
                        <p className="text-grey">{t("surveys:henuzTamamlanmisFormYok")}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            )}

            {/* Form Fill/View Modal */}
            {selectedForm && (
              <Dialog open={!!selectedForm} onOpenChange={(open) => !open && setSelectedForm(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      {(() => {
                        const IconComp = FORM_ICONS[selectedForm.icon] || FileText;
                        return <IconComp className="w-5 h-5" />;
                      })()}
                      {selectedForm.title || selectedForm.form_title}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="py-4 space-y-6">
                    {selectedForm.description && (
                      <p className="text-grey text-sm">{selectedForm.description}</p>
                    )}

                    {/* Questions */}
                    <div className="space-y-4">
                      {selectedForm.questions?.map((question, index) => (
                        <div key={question.question_id || index} className="p-4 border rounded-xl">
                          <Label className="block mb-2 font-medium">
                            {index + 1}. {question.text}
                            {question.is_required && <span className="text-red-500 ml-1">*</span>}
                          </Label>

                          {question.type === "text" && (
                            <Textarea
                              value={formAnswers[question.question_id] || ""}
                              onChange={(e) =>
                                setFormAnswers((prev) => ({
                                  ...prev,
                                  [question.question_id]: e.target.value,
                                }))
                              }
                              disabled={selectedForm.viewOnly && !selectedForm.editing}
                              placeholder={t("common:ph_cevabiniziYazin")}
                              className="mt-2 rounded-xl"
                            />
                          )}

                          {question.type === "boolean" && (
                            <div className="flex gap-3 mt-2">
                              <Button
                                type="button"
                                variant={
                                  formAnswers[question.question_id] === true ||
                                  formAnswers[question.question_id] === "true" ||
                                  formAnswers[question.question_id] === "Evet"
                                    ? "default"
                                    : "outline"
                                }
                                onClick={() =>
                                  !selectedForm.viewOnly &&
                                  setFormAnswers((prev) => ({
                                    ...prev,
                                    [question.question_id]: true,
                                  }))
                                }
                                disabled={selectedForm.viewOnly && !selectedForm.editing}
                                className="rounded-xl flex-1"
                              >
                                Evet
                              </Button>
                              <Button
                                type="button"
                                variant={
                                  formAnswers[question.question_id] === false ||
                                  formAnswers[question.question_id] === "false" ||
                                  formAnswers[question.question_id] === "Hayır"
                                    ? "default"
                                    : "outline"
                                }
                                onClick={() =>
                                  !selectedForm.viewOnly &&
                                  setFormAnswers((prev) => ({
                                    ...prev,
                                    [question.question_id]: false,
                                  }))
                                }
                                disabled={selectedForm.viewOnly && !selectedForm.editing}
                                className="rounded-xl flex-1"
                              >
                                Hayır
                              </Button>
                            </div>
                          )}

                          {question.type === "select" && question.options && (
                            <Select
                              value={formAnswers[question.question_id] || ""}
                              onValueChange={(v) =>
                                setFormAnswers((prev) => ({
                                  ...prev,
                                  [question.question_id]: v,
                                }))
                              }
                              disabled={selectedForm.viewOnly && !selectedForm.editing}
                            >
                              <SelectTrigger className="mt-2 rounded-xl">
                                <SelectValue placeholder={t("common:ph_seciniz")} />
                              </SelectTrigger>
                              <SelectContent>
                                {question.options.map((opt) => (
                                  <SelectItem key={opt} value={opt}>
                                    {opt}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {question.type === "multiselect" && question.options && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {question.options.map((opt) => {
                                const selected = (formAnswers[question.question_id] || []).includes(
                                  opt
                                );
                                return (
                                  <Button
                                    key={opt}
                                    type="button"
                                    variant={selected ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => {
                                      if (selectedForm.viewOnly && !selectedForm.editing) return;
                                      const current = formAnswers[question.question_id] || [];
                                      setFormAnswers((prev) => ({
                                        ...prev,
                                        [question.question_id]: selected
                                          ? current.filter((x) => x !== opt)
                                          : [...current, opt],
                                      }));
                                    }}
                                    disabled={selectedForm.viewOnly && !selectedForm.editing}
                                    className="rounded-xl"
                                  >
                                    {opt}
                                  </Button>
                                );
                              })}
                            </div>
                          )}

                          {question.type === "rating" && (
                            <div className="flex gap-2 mt-2">
                              {[1, 2, 3, 4, 5].map((rating) => (
                                <Button
                                  key={rating}
                                  type="button"
                                  variant={
                                    formAnswers[question.question_id] === rating ? "default" : "outline"
                                  }
                                  size="sm"
                                  onClick={() =>
                                    !selectedForm.viewOnly &&
                                    setFormAnswers((prev) => ({
                                      ...prev,
                                      [question.question_id]: rating,
                                    }))
                                  }
                                  disabled={selectedForm.viewOnly && !selectedForm.editing}
                                  className="rounded-xl w-10 h-10"
                                >
                                  {rating}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Edit History for completed forms */}
                    {selectedForm.viewOnly && selectedForm.edit_history?.length > 0 && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-xl">
                        <p className="font-medium text-amber-800 mb-2">{t("common:duzenlemeGecmisi")}</p>
                        <div className="space-y-1 text-sm text-amber-700">
                          {selectedForm.edit_history.map((edit, idx) => (
                            <p key={idx}>
                              {format(new Date(edit.edited_at), "d MMM yyyy HH:mm", { locale: getDateLocale() })}
                              {edit.reason && ` - ${edit.reason}`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedForm(null);
                          setFormAnswers({});
                        }}
                        className="flex-1 rounded-xl"
                      >
                        {selectedForm.viewOnly ? t("common:close") : t("reservations:cancelledRes")}
                      </Button>
                      {selectedForm.viewOnly ? (
                        selectedForm.editing ? (
                          <Button
                            onClick={handleUpdateForm}
                            disabled={submittingForm}
                            className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                          >
                            {submittingForm ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              t("memberDashboard:update")
                            )}
                          </Button>
                        ) : (
                          <Button
                            onClick={() =>
                              setSelectedForm((prev) => ({ ...prev, editing: true, viewOnly: true }))
                            }
                            className="flex-1 bg-amber-500 hover:bg-amber-600 rounded-xl"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            {t("memberDashboard:edit")}
                          </Button>
                        )
                      ) : (
                        <Button
                          onClick={handleSubmitForm}
                          disabled={submittingForm}
                          className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                          data-testid="submit-form-btn"
                        >
                          {submittingForm ? <Loader2 className="w-4 h-4 animate-spin" /> : t("memberDashboard:submit")}
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === "history" && (
            <div className="space-y-6">
            <h2 className="font-heading font-bold text-xl text-navy">{t("common:antrenmanGecmisi")}</h2>

            {trainingHistory.length > 0 ? (
              <div className="space-y-3">
                {trainingHistory.map((res) => (
                  <Card key={res.reservation_id} className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-green-50 rounded-xl flex flex-col items-center justify-center">
                          <span className="text-xs text-green-600">
                            {res.date ? format(new Date(res.date), "MMM", { locale: getDateLocale() }) : ""}
                          </span>
                          <span className="font-bold text-green-700 text-lg">
                            {res.date ? format(new Date(res.date), "d") : ""}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-navy">{getLocalizedBranch(res.branch)}</p>
                            {res.package_name && (
                              <Badge variant="outline" className="text-xs">
                                {res.package_name}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-grey">
                            {res.start_time} - {res.end_time} • {res.trainer_name}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-grey">{res.business_name}</span>
                            {res.session_number && res.total_sessions && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                {res.session_number}/{res.total_sessions}. Seans
                              </Badge>
                            )}
                          </div>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-12 text-center">
                  <Activity className="w-12 h-12 text-grey mx-auto mb-3" />
                  <p className="text-grey">{t("common:henuzTamamlanmisAntrenmanYok")}</p>
                </CardContent>
              </Card>
            )}

            {/* Measurements */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{t("evaluations:olcumlerim")}</CardTitle>
              </CardHeader>
              <CardContent>
                {measurements.length > 0 ? (
                  <div className="space-y-3">
                    {measurements.slice(0, 10).map((m, idx) => {
                      let dateStr = "-";
                      try {
                        const dateField = m.measured_at || m.date;
                        if (dateField) {
                          const date = new Date(dateField);
                          if (!isNaN(date.getTime())) {
                            dateStr = format(date, "dd/MM/yyyy HH:mm", { locale: getDateLocale() });
                          }
                        }
                      } catch (e) {
                        dateStr = "-";
                      }
                      return (
                        <div 
                          key={m.measurement_id || idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                        >
                          <div className="flex items-center gap-3">
                            <Activity className="w-5 h-5 text-navy" />
                            <div>
                              <p className="font-medium text-navy">
                                {getLocalizedMeasurement ? getLocalizedMeasurement(m.parameter) : m.parameter} {m.unit ? `(${m.unit})` : ''}
                              </p>
                              <p className="text-xs text-grey">{dateStr}</p>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-navy">
                            {m.value} {m.unit || ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-grey py-4">{t("evaluations:noMeasurementsYet")}</p>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="space-y-6">
            <h2 className="font-heading font-bold text-xl text-navy">{t("memberDashboard:myProfile")}</h2>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <Avatar className="w-20 h-20">
                    <AvatarFallback className="bg-navy text-white text-2xl">
                      {user?.name?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-navy">
                      {user?.name} {user?.surname}
                    </h3>
                    <p className="text-grey">{user?.email}</p>
                  </div>
                </div>

                {/* Profile Edit Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="profile-name">{t("common:firstName")}</Label>
                      <Input
                        id="profile-name"
                        value={profileForm?.name || ""}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                        placeholder={t("common:firstName")}
                        className="rounded-xl mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-surname">{t('common:surname')}</Label>
                      <Input
                        id="profile-surname"
                        value={profileForm?.surname || ""}
                        onChange={(e) => setProfileForm({...profileForm, surname: e.target.value})}
                        placeholder={t('common:surname')}
                        className="rounded-xl mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="profile-email">Email</Label>
                    <Input
                      id="profile-email"
                      type="email"
                      value={profileForm?.email || ""}
                      onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                      placeholder="Email"
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-phone">{t('common:phone')}</Label>
                    <Input
                      id="profile-phone"
                      value={profileForm?.phone || ""}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      placeholder="05XX XXX XX XX"
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-birth-date">{t('members:birthDate')}</Label>
                    <DatePickerTR
                      value={profileForm?.birth_date ? new Date(profileForm.birth_date) : null}
                      onChange={(date) => setProfileForm({...profileForm, birth_date: date ? format(date, "yyyy-MM-dd") : ""})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-gender">{t('common:gender')}</Label>
                    <Select
                      value={profileForm?.gender || ""}
                      onValueChange={(value) => setProfileForm({...profileForm, gender: value})}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue placeholder={t("common:ph_secin")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t('common:male')}</SelectItem>
                        <SelectItem value="female">{t('common:female')}</SelectItem>
                        <SelectItem value="other">{t('common:other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    className="w-full rounded-xl bg-navy hover:bg-navy/90"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {t("memberDashboard:saving")}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {t('common:saveProfile')}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Password Change Section */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {t("memberDashboard:passwordChangeTitle")}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPasswordChange(!showPasswordChange)}
                    className="rounded-xl"
                    data-testid="toggle-password-change-btn"
                  >
                    {showPasswordChange ? t("reservations:cancelledRes") : t("memberDashboard:changePassword")}
                  </Button>
                </div>
              </CardHeader>
              {showPasswordChange && (
                <CardContent className="pt-2">
                  <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                    {/* Current Password */}
                    <div className="space-y-2">
                      <Label>{t("common:currentPassword")}</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                          placeholder={t("common:ph_mevcutSifreniziGirin")}
                          className="pr-10 rounded-xl"
                          data-testid="current-password-input"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* New Password */}
                    <div className="space-y-2">
                      <Label>{t("common:newPassword")}</Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                          placeholder={t("common:ph_yeniSifreniziGirinEnAz")}
                          className="pr-10 rounded-xl"
                          data-testid="new-password-input"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <Label>{t("memberDashboard:confirmNewPassword")}</Label>
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        placeholder={t("common:ph_yeniSifreniziTekrarGirin")}
                        className="rounded-xl"
                        data-testid="confirm-password-input"
                        autoComplete="new-password"
                      />
                    </div>
                    
                    {/* Change Button */}
                    <Button
                      onClick={handleChangePassword}
                      disabled={savingPassword}
                      className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                      data-testid="change-password-submit-btn"
                    >
                      {savingPassword ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {t("memberDashboard:changing")}
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          {t("memberDashboard:changePasswordBtn")}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Invitation Code */}
            <InvitationCodeCard />

            {/* Memberships */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{t("common:myMemberships")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {memberships.map((m) => (
                    <div 
                      key={m.member_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-navy" />
                        <div>
                          <p className="font-medium text-navy">{m.business_name}</p>
                          <p className="text-sm text-grey">{t("memberDashboard:memberNo")} {m.member_no}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={m.is_active ? "bg-green-100 text-green-700" : ""}>
                          {m.is_active ? t("admin:active") : t("common:passive")}
                        </Badge>
                        <p className="text-sm text-grey mt-1">
                          {m.total_remaining_sessions} {t("memberDashboard:sessionsRemaining")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          )}

          {/* EVENTS TAB */}
          {activeTab === "events" && (
            <div className="space-y-6">
            {eventsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : (
              <>
                {/* Event Notifications Banner */}
                {eventNotifications.length > 0 && (
                  <Card className="border-0 shadow-sm bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-l-orange-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                        <AlertCircle className="w-5 h-5" />
                        Etkinlik Bildirimleri
                        <Badge className="bg-orange-100 text-orange-700 ml-2">
                          {eventNotifications.length}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {eventNotifications.map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className="flex items-start justify-between p-3 bg-white rounded-xl"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-navy">{notif.title}</p>
                            <p className="text-sm text-grey mt-1">{notif.message}</p>
                            {notif.data?.changes && notif.data.changes.length > 0 && (
                              <div className="mt-2 text-xs text-orange-600">
                                {t("memberDashboard:changes")} {notif.data.changes.join(", ")}
                              </div>
                            )}
                            <p className="text-xs text-grey mt-1">
                              {format(new Date(notif.created_at), "d MMMM HH:mm", { locale: getDateLocale() })}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkNotificationRead(notif.notification_id)}
                            className="text-orange-600 hover:bg-orange-100"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t("memberDashboard:seen")}
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {/* Upcoming Events */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-blue-600" />
                      {t("memberDashboard:upcomingEvents")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {upcomingEvents.length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <PartyPopper className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("events:noUpcomingEvents")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {upcomingEvents.map((event) => (
                          <div 
                            key={event.event_id}
                            className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                            onClick={() => handleOpenEventDetail(event.event_id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-navy">{event.title}</h3>
                                  {!event.is_free && event.price > 0 && (
                                    <Badge className="bg-green-100 text-green-700">
                                      {currencySymbol}{event.price}
                                    </Badge>
                                  )}
                                  {event.is_free && (
                                    <Badge className="bg-blue-100 text-blue-700">{t('common:free')}</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-grey mt-1 line-clamp-1">{event.description}</p>
                                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                                  <div className="flex items-center gap-1 text-grey">
                                    <CalendarIcon className="w-4 h-4" />
                                    {format(new Date(event.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })}
                                  </div>
                                  <div className="flex items-center gap-1 text-grey">
                                    <Clock className="w-4 h-4" />
                                    {event.start_time} - {event.end_time}
                                  </div>
                                  {event.location && (
                                    <div className="flex items-center gap-1 text-grey">
                                      <MapPin className="w-4 h-4" />
                                      {event.location}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1 text-grey">
                                    <Users className="w-4 h-4" />
                                    {event.participant_count}{event.max_participants ? `/${event.max_participants}` : ""} {t("memberDashboard:participants")}
                                  </div>
                                </div>
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                {event.is_joined ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleLeaveEvent(event.event_id)}
                                    disabled={joiningEvent === event.event_id}
                                    className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                                  >
                                    {joiningEvent === event.event_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <UserMinus className="w-4 h-4 mr-1" />
                                        {t("memberDashboard:leave")}
                                      </>
                                    )}
                                  </Button>
                                ) : event.is_full ? (
                                  <Badge className="bg-red-100 text-red-700">{t('reservations:full')}</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => handleJoinEvent(event.event_id)}
                                    disabled={joiningEvent === event.event_id}
                                    className="bg-navy hover:bg-navy/90 rounded-xl"
                                  >
                                    {joiningEvent === event.event_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <UserPlus className="w-4 h-4 mr-1" />
                                        {t("memberDashboard:join")}
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* My Joined Events */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      {t("memberDashboard:myJoinedEvents")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {myEvents.length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("events:noEventParticipation")}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {myEvents.map((event) => {
                          const isCompleted = event.status === "completed";
                          const isCancelled = event.status === "cancelled";
                          const isPast = new Date(event.date) < new Date(new Date().toDateString());
                          const canLeave = !isCompleted && !isCancelled && !isPast;
                          
                          return (
                            <div 
                              key={event.event_id}
                              className={`flex items-center justify-between p-4 rounded-xl cursor-pointer hover:opacity-90 transition-opacity ${
                                isCompleted ? "bg-blue-50" : isCancelled ? "bg-red-50" : "bg-green-50"
                              }`}
                              onClick={() => handleOpenEventDetail(event.event_id)}
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-navy">{event.title}</p>
                                  {!event.is_free && event.price > 0 && (
                                    <Badge variant="outline" className="text-xs">
                                      {currencySymbol}{event.price}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-grey">
                                  {format(new Date(event.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })} • {event.start_time}
                                </p>
                              </div>
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                {isCompleted ? (
                                  <Badge className="bg-blue-100 text-blue-700">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    {t("memberDashboard:completedEvent")}
                                  </Badge>
                                ) : isCancelled ? (
                                  <Badge className="bg-red-100 text-red-700">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    {t("memberDashboard:cancelledEvent")}
                                  </Badge>
                                ) : canLeave ? (
                                  <>
                                    <Badge className="bg-green-100 text-green-700">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      {t("memberDashboard:youAreJoined")}
                                    </Badge>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleLeaveEvent(event.event_id)}
                                      disabled={joiningEvent === event.event_id}
                                      className="text-red-500 hover:bg-red-50"
                                    >
                                      {joiningEvent === event.event_id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <UserMinus className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {t("memberDashboard:youAreJoined")}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          )}

          {/* SURVEYS TAB */}
          {activeTab === "surveys" && (
            <div className="space-y-6">
            {surveysLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : (
              <>
                {/* Pending Surveys */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-amber-600" />
                      {t("memberDashboard:pendingSurveys")}
                      {pendingSurveys.length > 0 && (
                        <Badge className="bg-amber-100 text-amber-700 ml-2">
                          {pendingSurveys.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingSurveys.length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("surveys:tamamlanmamisAnketBulunmuyor")}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingSurveys.map((survey) => (
                          <div 
                            key={survey.survey_id}
                            className="flex items-center justify-between p-4 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
                            onClick={() => handleSelectSurvey(survey)}
                          >
                            <div>
                              <p className="font-medium text-navy">{survey.title}</p>
                              <p className="text-sm text-grey">{survey.description}</p>
                              <p className="text-xs text-amber-600 mt-1">
                                {survey.question_count || 0} soru
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-grey" />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Completed Surveys */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      {t("memberDashboard:completedSurveys")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {completedSurveys.length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("surveys:henuzTamamlananAnketYok")}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {completedSurveys.map((survey) => (
                          <div 
                            key={survey.response_id || survey.survey_id}
                            className="flex items-center justify-between p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer"
                            onClick={async () => {
                              setLoadingSurveyResponse(true);
                              setViewingSurveyResponse(survey);
                              // Fetch full response details if needed
                              try {
                                const token = localStorage.getItem('auth_token');
                                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
                                const response = await fetch(`${API}/surveys/responses/${survey.response_id || survey.survey_id}`, {
                                  credentials: "include",
                                  headers,
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setViewingSurveyResponse(prev => ({...prev, ...data}));
                                }
                              } catch (error) {
                                console.error("Error fetching survey response:", error);
                              } finally {
                                setLoadingSurveyResponse(false);
                              }
                            }}
                          >
                            <div className="flex-1">
                              <p className="font-semibold text-navy">{survey.survey_title || survey.title}</p>
                              <p className="text-sm text-grey">{survey.survey_type_name || survey.type_name || t("memberDashboard:survey")}</p>
                              <p className="text-xs text-grey mt-1">
                                {t("memberDashboard:completedOn")} {survey.created_at || survey.submitted_at ? format(new Date(survey.created_at || survey.submitted_at), "d MMMM yyyy, HH:mm", { locale: getDateLocale() }) : "-"}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t("memberDashboard:completedEvent")}
                              </Badge>
                              <ChevronRight className="w-5 h-5 text-grey" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          )}

          {/* EVALUATIONS TAB */}
          {activeTab === "evaluations" && (
            <div className="space-y-6">
            {evaluationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : (
              <>
                {/* Header with Mark All Read */}
                {evaluations.length > 0 && unreadEvaluationCount > 0 && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAllRead}
                      className="rounded-xl"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {t("memberDashboard:markAllRead")}
                    </Button>
                  </div>
                )}

                {/* Evaluations List */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      {t("memberDashboard:trainerEvaluations")}
                      {unreadEvaluationCount > 0 && (
                        <Badge className="bg-red-100 text-red-700 ml-2">
                          {unreadEvaluationCount} {t("memberDashboard:new")}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {evaluations.length === 0 ? (
                      <div className="text-center py-12 text-grey">
                        <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium">{t("evaluations:noEvaluationsYet")}</p>
                        <p className="text-sm mt-1">{t("evaluations:antrenorunuzDegerlendirmeYazdigindaBurad")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {evaluations.map((evaluation) => {
                          const CategoryIcon = EVAL_CATEGORY_ICONS[evaluation.category] || ClipboardList;
                          return (
                            <div 
                              key={evaluation.evaluation_id}
                              className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
                                !evaluation.is_read 
                                  ? "bg-blue-50 border-blue-200" 
                                  : "bg-white border-slate-200"
                              }`}
                              onClick={() => {
                                setSelectedEvaluation(evaluation);
                                if (!evaluation.is_read) {
                                  handleMarkEvaluationRead(evaluation.evaluation_id);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3 flex-1">
                                  <div className={`p-2 rounded-xl ${EVAL_CATEGORY_COLORS[evaluation.color] || "bg-slate-100"}`}>
                                    <CategoryIcon className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-navy">{evaluation.title}</span>
                                      {!evaluation.is_read && (
                                        <Badge className="bg-blue-500 text-white text-xs">{t('common:new')}</Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-grey line-clamp-2">{evaluation.content}</p>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-grey">
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {evaluation.trainer_name}
                                      </span>
                                      <span>
                                        {format(new Date(evaluation.created_at), "d MMMM yyyy", { locale: getDateLocale() })}
                                      </span>
                                      {evaluation.rating && (
                                        <span className="flex items-center gap-1">
                                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                          {evaluation.rating}/5
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-grey" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          )}

          {/* GOALS & BADGES TAB */}
          {activeTab === "goals" && (
            <div className="space-y-6">
            {goalsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : (
              <>
                {/* Stats Overview */}
                {goalStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 text-center">
                        <Target className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                        <p className="text-2xl font-bold text-navy">{goalStats.active_goals}</p>
                        <p className="text-sm text-grey">{t("memberDashboard:activeGoal")}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p className="text-2xl font-bold text-navy">{goalStats.completed_goals}</p>
                        <p className="text-sm text-grey">{t('common:completed')}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 text-center">
                        <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                        <p className="text-2xl font-bold text-navy">{goalStats.total_badges}</p>
                        <p className="text-sm text-grey">{t("memberDashboard:badge")}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 text-center">
                        <TrendingUp className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                        <p className="text-2xl font-bold text-navy">{goalStats.total_goals}</p>
                        <p className="text-sm text-grey">{t("memberDashboard:totalGoal")}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Active Goals */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      {t("memberDashboard:myActiveGoals")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {goals.filter(g => g.status === "active").length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("memberDashboard:noActiveGoal")}</p>
                        <p className="text-sm mt-1">{t("evaluations:antrenorunuzSizeHedefBelirledigindeBurad")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {goals.filter(g => g.status === "active").map((goal) => (
                          <div key={goal.goal_id} className="p-4 bg-slate-50 rounded-xl">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-navy">{goal.title}</p>
                                {goal.description && (
                                  <p className="text-sm text-grey">{goal.description}</p>
                                )}
                              </div>
                              <Badge className={goal.period === "weekly" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                                {goal.period === "weekly" ? t("common:weekly") : t("common:monthly")}
                              </Badge>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-grey">
                                  {goal.current_value} / {goal.target_value} {goal.goal_type === "sessions" ? t("memberDashboard:sessions") : ""}
                                </span>
                                <span className="font-medium text-navy">{getGoalProgress(goal)}%</span>
                              </div>
                              <Progress value={getGoalProgress(goal)} className="h-3" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Badges */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-600" />
                      {t("memberDashboard:myBadges")}
                      {unseenBadgeCount > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-700 ml-2">
                          {unseenBadgeCount} {t("memberDashboard:new")}
                        </Badge>
                      )}
                    </CardTitle>
                    {unseenBadgeCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMarkBadgesSeen}
                        className="text-grey"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t("memberDashboard:badgeSeen")}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {badges.length === 0 ? (
                      <div className="text-center py-8 text-grey">
                        <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t("common:henuzRozetKazanilmamis")}</p>
                        <p className="text-sm mt-1">{t("memberDashboard:earnBadges")}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {badges.map((badge) => {
                          const Icon = BADGE_ICONS[badge.badge_icon] || Trophy;
                          const colorClass = BADGE_COLORS[badge.badge_color] || BADGE_COLORS.gold;
                          
                          return (
                            <div
                              key={badge.member_badge_id}
                              className={`p-4 rounded-xl border-2 ${colorClass} text-center relative ${!badge.is_seen ? "ring-2 ring-yellow-400" : ""}`}
                            >
                              {!badge.is_seen && (
                                <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  Yeni!
                                </span>
                              )}
                              <Icon className="w-10 h-10 mx-auto mb-2" />
                              <p className="font-semibold text-sm">{badge.badge_name}</p>
                              <p className="text-xs mt-1 opacity-75">{badge.badge_description}</p>
                              <p className="text-xs mt-2 opacity-60">
                                {format(new Date(badge.awarded_at), "d MMM yyyy", { locale: getDateLocale() })}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Completed Goals */}
                {goals.filter(g => g.status === "completed").length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        {t("memberDashboard:completedGoals")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {goals.filter(g => g.status === "completed").map((goal) => (
                          <div key={goal.goal_id} className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                            <div>
                              <p className="font-medium text-navy">{goal.title}</p>
                              <p className="text-sm text-grey">
                                {goal.target_value} {goal.goal_type === "sessions" ? t("memberDashboard:sessions") : ""} {t("memberDashboard:sessionsCompleted")}
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t("memberDashboard:successful")}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
          )}
          
          {/* Messages Tab */}
          {activeTab === "messages" && (
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-xl text-navy flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                {t("memberDashboard:messagesTitle")}
              </h2>
              <MessagingModule 
                user={user} 
                businessId={selectedBusiness === "all" ? memberships[0]?.business_id : selectedBusiness}
                onUnreadCountChange={fetchUnreadMessageCount}
              />
            </div>
          )}
        </main>
      </div>

      {/* Reservation Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={(open) => {
        setShowRequestModal(open);
        if (!open) {
          // Reset form when modal closes
          setRequestForm({
            business_id: "",
            trainer_id: "",
            room_id: "",
            branch: "",
            date: "",
            start_time: "",
            end_time: "",
            training_type: "one_on_one",
            member_package_id: "",
            notes: ""
          });
          setRequestType("");
          setSelectedPackageInfo(null);
          setRoomAvailability(null);
          setTrainerAvailability(null);
          setMemberTimeConflict(null);
        }
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-navy" />
              {t("memberDashboard:reservationRequestTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Business Selection */}
            <div>
              <Label>{t("memberDashboard:businessLabel")}</Label>
              <Select 
                value={requestForm.business_id} 
                onValueChange={(v) => {
                  setRequestForm(prev => ({ ...prev, business_id: v, trainer_id: "", room_id: "", member_package_id: "", branch: "" }));
                  fetchBusinessData(v);
                  setSelectedPackageInfo(null);
                  setRequestType("");
                }}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_i̇sletmeSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {memberships.map((m) => (
                    <SelectItem key={m.business_id} value={m.business_id}>
                      {m.business_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Request Type: Package, Card or Demo - İşletme formuyla aynı mantık */}
            {requestForm.business_id && (
              <div>
                <Label>{t("memberDashboard:requestType")}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    type="button"
                    variant={requestType === "package" ? "default" : "outline"}
                    className={`flex-1 min-w-[80px] rounded-xl h-10 ${requestType === "package" ? "bg-navy" : ""}`}
                    onClick={() => {
                      setRequestType("package");
                      setSelectedCard("");
                      setRequestForm(prev => ({ ...prev, branch: "", member_package_id: "" }));
                      setSelectedPackageInfo(null);
                    }}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    {t("memberDashboard:package")}
                  </Button>
                  <Button
                    type="button"
                    variant={requestType === "card" ? "default" : "outline"}
                    className={`flex-1 min-w-[80px] rounded-xl h-10 ${requestType === "card" ? "bg-navy" : ""}`}
                    onClick={() => {
                      setRequestType("card");
                      setRequestForm(prev => ({ ...prev, branch: "", member_package_id: "" }));
                      setSelectedPackageInfo(null);
                    }}
                    disabled={!memberships.find(m => m.business_id === requestForm.business_id)?.member_card}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t("memberDashboard:card")} {!memberships.find(m => m.business_id === requestForm.business_id)?.member_card && "❌"}
                  </Button>
                  <Button
                    type="button"
                    variant={requestType === "demo" ? "default" : "outline"}
                    className={`flex-1 min-w-[80px] rounded-xl h-10 ${requestType === "demo" ? "bg-green-600" : ""}`}
                    onClick={() => {
                      setRequestType("demo");
                      setSelectedCard("");
                      setRequestForm(prev => ({ ...prev, branch: "", member_package_id: "" }));
                      setSelectedPackageInfo(null);
                    }}
                  >
                    <Dumbbell className="w-4 h-4 mr-2" />
                    Demo
                  </Button>
                </div>
              </div>
            )}

            {/* If Package selected - Show package dropdown */}
            {requestType === "package" && (
              <div>
                <Label>{t("memberDashboard:selectPackage")}</Label>
                <select
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  value={requestForm.member_package_id}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setSelectedPackageInfo(null);
                      setRequestForm(prev => ({ ...prev, member_package_id: "", branch: "" }));
                      return;
                    }
                    const pkg = memberPackages.find(p => p.member_package_id === v);
                    if (pkg) {
                      setSelectedPackageInfo(pkg);
                      const pkgBranches = pkg.package_branches && pkg.package_branches.length > 0
                        ? pkg.package_branches
                        : (pkg.package_branch ? [pkg.package_branch] : (pkg.branch ? [pkg.branch] : []));
                      // Auto-fill only when single branch; combo → user picks below
                      const autoBranch = pkgBranches.length === 1 ? pkgBranches[0] : "";
                      setRequestForm(prev => ({ 
                        ...prev, 
                        member_package_id: v,
                        branch: autoBranch
                      }));
                    }
                  }}
                  data-testid="member-request-package-select"
                >
                  <option value="">{t("memberDashboard:selectPackagePh")}</option>
                  {memberPackages.filter(p => (p.is_active !== false && (p.status === 'active' || !p.status)) && p.remaining_sessions > 0).map((p) => {
                    const branchesList = (p.package_branches && p.package_branches.length > 0)
                      ? p.package_branches
                      : (p.package_branch ? [p.package_branch] : (p.branch ? [p.branch] : []));
                    const branchText = branchesList.length >= 2 ? `🔗 ${branchesList.join(" + ")}` : branchesList[0];
                    return (
                      <option key={p.member_package_id} value={p.member_package_id}>
                        {p.package_no ? `[${p.package_no}] ` : ""}{p.package_name}{branchText ? ` (${branchText})` : ''} - {t("memberDashboard:remainingLessons")} {p.remaining_sessions} {t("memberDashboard:lessons")}
                      </option>
                    );
                  })}
                </select>
                
                {/* Show selected package branch — combo paket için dropdown */}
                {selectedPackageInfo && (() => {
                  const pkgBranches = selectedPackageInfo.package_branches && selectedPackageInfo.package_branches.length > 0
                    ? selectedPackageInfo.package_branches
                    : (selectedPackageInfo.package_branch ? [selectedPackageInfo.package_branch] : (selectedPackageInfo.branch ? [selectedPackageInfo.branch] : []));
                  const isCombo = pkgBranches.length >= 2;
                  return (
                  <div className="mt-3 p-3 bg-slate-50 rounded-xl">
                    <p className="text-sm text-grey flex items-center gap-2">
                      {t("common:branch")}
                      {isCombo && (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 font-medium">🔗 Combo</span>
                      )}
                    </p>
                    {isCombo ? (
                      <Select
                        value={requestForm.branch}
                        onValueChange={(v) => setRequestForm(prev => ({ ...prev, branch: v }))}
                      >
                        <SelectTrigger className="rounded-xl mt-1" data-testid="member-combo-branch-select">
                          <SelectValue placeholder="Bu derste hangi branş?" />
                        </SelectTrigger>
                        <SelectContent>
                          {pkgBranches.map((b) => (
                            <SelectItem key={b} value={b}>
                              {getLocalizedBranch ? getLocalizedBranch(b) : b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-semibold text-navy">{getLocalizedBranch(pkgBranches[0]) || t("memberDashboard:notSpecified")}</p>
                    )}
                    <p className="text-xs text-grey mt-1">
                      {t("memberDashboard:sessionDuration")} {selectedPackageInfo.session_duration || 60} {t("memberDashboard:min")}
                    </p>
                  </div>
                  );
                })()}
              </div>
            )}

            {/* If Demo selected - Show branch dropdown */}
            {requestType === "demo" && branches.length > 0 && (
              <div>
                <Label>{t("memberDashboard:selectBranch")}</Label>
                <Select 
                  value={requestForm.branch} 
                  onValueChange={(v) => setRequestForm(prev => ({ ...prev, branch: v }))}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:ph_demoIcinBransSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b} (Demo: {branchDemoDurations[b] || 30} dk)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Demo info */}
            {requestType === "demo" && (
              <div className="bg-green-50 p-3 rounded-xl">
                <p className="text-sm text-green-700">
                  <strong>{getLocalizedBranch(requestForm.branch)}</strong> {t("memberDashboard:demoInfo")}
                  <br />
                  <span className="text-xs">
                    {t("memberDashboard:demoDuration")} {branchDemoDurations[requestForm.branch] || 30} {t("memberDashboard:minutes")}
                  </span>
                </p>
              </div>
            )}

            {/* If Card selected - Show branch and training type selection */}
            {requestType === "card" && branches.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t("memberDashboard:branchLabel")}</Label>
                  <Select 
                    value={requestForm.branch} 
                    onValueChange={(v) => setRequestForm(prev => ({ ...prev, branch: v }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_bransSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("memberDashboard:trainingType")}</Label>
                  <Select 
                    value={requestForm.training_type} 
                    onValueChange={(v) => setRequestForm(prev => ({ ...prev, training_type: v }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_tipSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_on_one">{t('trainers:privateLesson')}</SelectItem>
                      <SelectItem value="duet">{t('reservations:duet')}</SelectItem>
                      <SelectItem value="group">{t('reservations:group')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Card info */}
            {requestType === "card" && memberships.find(m => m.business_id === requestForm.business_id)?.member_card && (
              <div className="bg-blue-50 p-3 rounded-xl">
                <p className="text-sm text-blue-700">
                  <strong>{t("memberDashboard:card")}:</strong> {memberships.find(m => m.business_id === requestForm.business_id)?.member_card}
                  <br />
                  <span className="text-xs">{t("memberDashboard:cardInfo")}</span>
                </p>
              </div>
            )}

            {/* Trainer - show after type selection */}
            {((requestType === "package" && requestForm.member_package_id) || requestType === "demo" || (requestType === "card" && requestForm.branch)) && (
              <div>
                <Label>{t("common:trainerRequired")}</Label>
                <Select 
                  value={requestForm.trainer_id} 
                  onValueChange={(v) => setRequestForm(prev => ({ ...prev, trainer_id: v }))}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    {trainers
                      .filter(t => t.is_active !== false)
                      .filter(t => {
                        // Filter by branch - trainer must know the selected branch
                        if (!requestForm.branch) return true;
                        const trainerBranches = t.branches || [];
                        return trainerBranches.includes(requestForm.branch);
                      })
                      .map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {requestForm.branch && trainers.filter(t => t.is_active !== false && (t.branches || []).includes(requestForm.branch)).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">{t("common:buBransiBilenAntrenorBulunamadi")}</p>
                )}
              </div>
            )}

            {/* Room - Branch filtered STRICTLY */}
            {requestForm.trainer_id && rooms.length > 0 && (
              <div>
                <Label>{t("memberDashboard:roomLabel")}</Label>
                <Select 
                  value={requestForm.room_id} 
                  onValueChange={(v) => setRequestForm(prev => ({ ...prev, room_id: v }))}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:ph_odaSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms
                      .filter(r => {
                        if (r.is_active === false) return false;
                        // STRICT: Branş seçiliyse sadece o branşı destekleyen odalar
                        if (requestForm.branch) {
                          const roomBranches = r.branches || [];
                          // Oda branşı tanımlıysa, seçilen branşı içermeli
                          if (roomBranches.length > 0) {
                            return roomBranches.includes(requestForm.branch);
                          }
                          // Oda branşı tanımlı değilse (genel oda) göster
                          return true;
                        }
                        return true;
                      })
                      .map((r) => (
                        <SelectItem key={r.room_id} value={r.room_id}>
                          {r.name} ({t("memberDashboard:capacity")} {r.capacity})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {requestForm.branch && rooms.filter(r => r.is_active !== false && (!(r.branches?.length > 0) || (r.branches || []).includes(requestForm.branch))).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">{t("memberDashboard:noRoomForBranch")} ({getLocalizedBranch(requestForm.branch)})</p>
                )}
              </div>
            )}

            {/* Date */}
            {requestForm.room_id && (
              <div>
                <Label>{t("common:dateRequired")}</Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-xl mt-1"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {requestForm.date ? (
                        format(new Date(requestForm.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })
                      ) : (
                        <span className="text-muted-foreground">{t('common:selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={requestForm.date ? new Date(requestForm.date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          setRequestForm(prev => ({ ...prev, date: formattedDate, start_time: "" }));
                          setDatePickerOpen(false); // Close popover after selection
                        }
                      }}
                      locale={tr}
                      disabled={(date) => {
                        // Geçmiş günleri devre dışı bırak
                        if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                        // İşletme kapalı günleri devre dışı bırak
                        const dayOfWeek = date.getDay();
                        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const dayName = dayNames[dayOfWeek];
                        const businessDay = businessWorkingHours[dayName];
                        if (businessDay?.closed === true) return true;
                        return false;
                      }}
                      initialFocus={false}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Time */}
            {requestForm.date && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("memberDashboard:startTime")}</Label>
                  {validTimeSlots.length > 0 ? (
                    <Select 
                      value={requestForm.start_time} 
                      onValueChange={(v) => {
                        // Geçmiş saati engelle (bugün için)
                        const today = todayLocalString();
                        if (requestForm.date === today) {
                          const now = new Date();
                          const [h, m] = v.split(":").map(Number);
                          const selectedTime = new Date();
                          selectedTime.setHours(h, m, 0, 0);
                          if (selectedTime <= now) {
                            toast.error(t("common:toast_gecmisBirSaatSecemezsiniz"));
                            return;
                          }
                        }
                        setRequestForm(prev => ({ ...prev, start_time: v }));
                      }}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_saatSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {validTimeSlots.filter(slot => {
                          // Bugün için geçmiş saatleri filtrele
                          const today = todayLocalString();
                          if (requestForm.date === today) {
                            const now = new Date();
                            const [h, m] = slot.split(":").map(Number);
                            const slotTime = new Date();
                            slotTime.setHours(h, m, 0, 0);
                            return slotTime > now;
                          }
                          return true;
                        }).map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            {slot}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600">
                      {t("memberDashboard:noAvailableTime")}
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t('settings:endTime')}</Label>
                  <div className="p-3 bg-slate-100 rounded-xl text-sm h-[42px] flex items-center">
                    {requestForm.end_time || t("memberDashboard:endTimeAuto")} 
                    {requestType === "package" && selectedPackageInfo?.session_duration && (
                      <span className="text-grey ml-1">({selectedPackageInfo.session_duration} dk)</span>
                    )}
                    {requestType === "demo" && requestForm.branch && (
                      <span className="text-grey ml-1">(Demo: {branchDemoDurations[requestForm.branch] || 30} dk)</span>
                    )}
                    {requestType === "card" && requestForm.branch && (
                      <span className="text-grey ml-1">({branchDemoDurations[requestForm.branch] || 60} dk)</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Working Hours Info */}
            {requestForm.date && requestForm.trainer_id && (
              <div className="p-3 bg-blue-50 rounded-xl space-y-1">
                <p className="font-medium text-blue-700 text-sm">{t('settings:workingHours')}</p>
                {(() => {
                  const dayOfWeek = new Date(requestForm.date).getDay();
                  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                  const dayName = dayNames[dayOfWeek];
                  const businessDay = businessWorkingHours[dayName];
                  const trainerDaySchedules = trainerSchedules[requestForm.trainer_id] || [];
                  const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
                  
                  return (
                    <>
                      <p className="text-xs text-blue-600">
                        {t("memberDashboard:businessHours")} {businessDay?.closed ? t("business:closed") : `${businessDay?.open || "08:00"} - ${businessDay?.close || "22:00"}`}
                      </p>
                      <p className="text-xs text-blue-600">
                        {t("memberDashboard:trainerHours")} {trainerDaySchedules.length > 0 
                          ? (trainerDaySchedule 
                              ? `${trainerDaySchedule.start_time} - ${trainerDaySchedule.end_time}` 
                              : t("memberDashboard:notWorkingToday"))
                          : t("memberDashboard:noScheduleDefined")}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Availability Status */}
            {(roomAvailability || trainerAvailability || memberTimeConflict) && requestForm.start_time && (
              <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                <p className="font-medium text-navy text-sm">{t("common:availabilityStatus")}</p>
                
                {/* Member time conflict */}
                {memberTimeConflict && (
                  <div className={`flex items-center gap-2 text-sm ${memberTimeConflict.has_conflict ? "text-red-600" : "text-green-600"}`}>
                    {memberTimeConflict.has_conflict ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>{t("memberDashboard:yourCalendar")} {memberTimeConflict.has_conflict ? memberTimeConflict.message : t("memberDashboard:thisTimeEmpty")}</span>
                  </div>
                )}
                
                {roomAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${roomAvailability.is_locked ? "text-red-600" : roomAvailability.available_capacity > 0 ? "text-green-600" : "text-red-600"}`}>
                    {roomAvailability.is_locked || roomAvailability.available_capacity <= 0 ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>{t("memberDashboard:roomStatus")} {roomAvailability.is_locked ? t("memberDashboard:locked") : roomAvailability.available_capacity > 0 ? `${t("memberDashboard:available")} (${roomAvailability.available_capacity} ${t("memberDashboard:personSlots")})` : t("reservations:lessonFull")}</span>
                  </div>
                )}
                {trainerAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${trainerAvailability.is_available ? "text-green-600" : "text-red-600"}`}>
                    {trainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>
                      {t("memberDashboard:trainerStatus")} {trainerAvailability.is_available 
                        ? (() => {
                            // ATM > 1 ise açıklama ile göster (Düet, Trio vb.)
                            const info = trainerAvailability.training_type_info;
                            if (info && info.max_participants > 1) {
                              return `${t("memberDashboard:available")} (${info.type} ders için ${info.remaining} ${t("memberDashboard:personSlots")})`;
                            }
                            // ATM = 1 ise sadece "Müsait" (birebir ders)
                            return t("memberDashboard:available");
                          })()
                        : (trainerAvailability.reason || t("memberDashboard:notAvailable"))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {requestForm.start_time && (
              <div>
                <Label>{t("common:noteOptional")}</Label>
                <Textarea
                  placeholder={t("common:ph_i̇steginizleIlgiliNotEkleyebilirsiniz")}
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            )}

            <Button
              className="w-full bg-navy hover:bg-navy/90 rounded-xl"
              onClick={handleCreateRequest}
              disabled={
                !requestForm.business_id || 
                !requestForm.branch ||
                !requestType ||
                (requestType === "package" && !requestForm.member_package_id) ||
                (requestType === "card" && !requestForm.training_type) ||
                !requestForm.trainer_id || 
                !requestForm.room_id ||
                !requestForm.date || 
                !requestForm.start_time ||
                sendingRequest ||
                (memberTimeConflict && memberTimeConflict.has_conflict) ||
                (trainerAvailability && !trainerAvailability.is_available) ||
                (roomAvailability && (roomAvailability.is_locked || roomAvailability.available_capacity <= 0))
              }
            >
              {sendingRequest && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {sendingRequest ? t("memberDashboard:sending") : t("memberDashboard:sendRequest")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Request Modal */}
      <Dialog open={showChangeModal} onOpenChange={(open) => {
        if (!open) {
          setShowChangeModal(false);
          // Reset form when modal closes
          setChangeForm({
            new_date: "",
            new_start_time: "",
            new_end_time: "",
            trainer_id: "",
            room_id: "",
            reason: ""
          });
          setChangeRoomAvailability(null);
          setChangeTrainerAvailability(null);
          setChangeMemberTimeConflict(null);
          setChangeValidTimeSlots([]);
        }
        // Modal açma işlemi handleOpenChangeModal fonksiyonunda yapılıyor
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-navy" />
              {t("memberDashboard:changeRequestTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Reservation Info */}
            {selectedReservation && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-sm text-grey">{t("memberDashboard:currentReservation")}</p>
                <p className="font-medium text-navy">
                  {selectedReservation.date && format(new Date(selectedReservation.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })}
                </p>
                <p className="text-sm font-medium">
                  {selectedReservation.start_time || selectedReservation.time} - {selectedReservation.end_time}
                </p>
                <p className="text-sm text-grey">{getLocalizedBranch(selectedReservation.branch)} • {selectedReservation.trainer_name}</p>
                {selectedReservation.room_name && (
                  <p className="text-xs text-grey mt-1">{t("memberDashboard:roomColon")} {selectedReservation.room_name}</p>
                )}
              </div>
            )}

            {/* Trainer Selection */}
            <div>
              <Label>{t("common:trainerRequired")}</Label>
              {(loadingBusinessData || (showChangeModal && trainers.length === 0 && selectedReservation?.business_id)) ? (
                <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("memberDashboard:trainersLoading")}
                </div>
              ) : trainers.length === 0 ? (
                <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600">
                  {t("memberDashboard:noTrainerFound")}
                </div>
              ) : (
                <>
                  <Select 
                    value={changeForm.trainer_id} 
                    onValueChange={(v) => setChangeForm(prev => ({ ...prev, trainer_id: v, new_start_time: "", room_id: "" }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:selectTrainer")} />
                    </SelectTrigger>
                    <SelectContent>
                      {trainers
                        .filter(t => t.is_active !== false)
                        .filter(t => {
                          // Filter by branch - trainer must know the reservation's branch
                          if (!selectedReservation?.branch) return true;
                          const trainerBranches = t.branches || [];
                          return trainerBranches.includes(selectedReservation.branch);
                        })
                        .map((t) => (
                          <SelectItem key={t.trainer_id} value={t.trainer_id}>
                            {t.name} {t.surname}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedReservation?.branch && trainers.filter(t => t.is_active !== false && (t.branches || []).includes(selectedReservation.branch)).length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">{t("memberDashboard:noTrainerForBranch")} ({getLocalizedBranch(selectedReservation.branch)})</p>
                  )}
                </>
              )}
            </div>

            {/* Room Selection - Branch filtered STRICTLY */}
            {changeForm.trainer_id && rooms.length > 0 && (
              <div>
                <Label>{t("memberDashboard:roomLabel")}</Label>
                <Select 
                  value={changeForm.room_id} 
                  onValueChange={(v) => setChangeForm(prev => ({ ...prev, room_id: v }))}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:ph_odaSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms
                      .filter(r => {
                        if (r.is_active === false) return false;
                        // STRICT: Rezervasyonun branşına göre filtrele
                        if (selectedReservation?.branch) {
                          const roomBranches = r.branches || [];
                          if (roomBranches.length > 0) {
                            return roomBranches.includes(selectedReservation.branch);
                          }
                          return true; // Genel oda
                        }
                        return true;
                      })
                      .map((r) => (
                        <SelectItem key={r.room_id} value={r.room_id}>
                          {r.name} (Kapasite: {r.capacity})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {selectedReservation?.branch && rooms.filter(r => r.is_active !== false && (!(r.branches?.length > 0) || (r.branches || []).includes(selectedReservation.branch))).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">{t("memberDashboard:noRoomForBranch")} ({getLocalizedBranch(selectedReservation.branch)})</p>
                )}
              </div>
            )}

            {/* Date Selection with Calendar */}
            {changeForm.room_id && (
              <div>
                <Label>{t("memberDashboard:newDate")}</Label>
                <Popover open={changeDatePickerOpen} onOpenChange={setChangeDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-xl mt-1"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {changeForm.new_date ? (
                        format(new Date(changeForm.new_date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })
                      ) : (
                        <span className="text-muted-foreground">{t('common:selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={changeForm.new_date ? new Date(changeForm.new_date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          setChangeForm(prev => ({ ...prev, new_date: formattedDate, new_start_time: "" }));
                          setChangeDatePickerOpen(false);
                        }
                      }}
                      locale={tr}
                      disabled={(date) => {
                        // Disable past dates
                        if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                        // Disable business closed days
                        const dayOfWeek = date.getDay();
                        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const dayName = dayNames[dayOfWeek];
                        const businessDay = businessWorkingHours[dayName];
                        if (businessDay?.closed === true) return true;
                        return false;
                      }}
                      initialFocus={false}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Time Selection */}
            {changeForm.new_date && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("memberDashboard:newStartTime")}</Label>
                  {changeValidTimeSlots.length > 0 ? (
                    <Select 
                      value={changeForm.new_start_time} 
                      onValueChange={(v) => {
                        // Check for past time if today
                        const today = todayLocalString();
                        if (changeForm.new_date === today) {
                          const now = new Date();
                          const [h, m] = v.split(":").map(Number);
                          const selectedTime = new Date();
                          selectedTime.setHours(h, m, 0, 0);
                          if (selectedTime <= now) {
                            toast.error(t("common:toast_gecmisBirSaatSecemezsiniz"));
                            return;
                          }
                        }
                        // Auto-calculate end time based on branch duration
                        let endTime = v;
                        if (selectedReservation && v) {
                          // Branşa göre süre belirle
                          const branchDurations = {
                            "EMS": 30,
                            "Pilates": 60,
                            "Yoga": 60,
                            "Fitness": 60,
                            "Kickbox": 60,
                            "CrossFit": 60,
                            "Fonksiyonel": 45,
                            "Zumba": 60
                          };
                          const branch = selectedReservation.branch || "";
                          const duration = branchDurations[branch] || selectedReservation.duration || 60;
                          const [h, m] = v.split(':').map(Number);
                          const endMins = h * 60 + m + duration;
                          const endH = Math.floor(endMins / 60);
                          const endM = endMins % 60;
                          endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                        }
                        setChangeForm(prev => ({ ...prev, new_start_time: v, new_end_time: endTime }));
                      }}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_saatSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {changeValidTimeSlots.filter(slot => {
                          // Filter past times for today
                          const today = todayLocalString();
                          if (changeForm.new_date === today) {
                            const now = new Date();
                            const [h, m] = slot.split(":").map(Number);
                            const slotTime = new Date();
                            slotTime.setHours(h, m, 0, 0);
                            return slotTime > now;
                          }
                          return true;
                        }).map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            {slot}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600">
                      {t("memberDashboard:noAvailableTimeForDate")}
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t("common:yeniBitis")}</Label>
                  <div className="p-3 bg-slate-100 rounded-xl text-sm h-[42px] flex items-center">
                    {changeForm.new_end_time || "Otomatik"} 
                    <span className="text-grey ml-1">
                      ({(() => {
                        const branchDurations = { "EMS": 30, "Pilates": 60, "Yoga": 60, "Fitness": 60, "Kickbox": 60, "CrossFit": 60, "Fonksiyonel": 45, "Zumba": 60 };
                        return branchDurations[selectedReservation?.branch] || selectedReservation?.duration || 60;
                      })()} dk)
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Working Hours Info */}
            {changeForm.new_date && changeForm.trainer_id && (
              <div className="p-3 bg-blue-50 rounded-xl space-y-1">
                <p className="font-medium text-blue-700 text-sm">{t('settings:workingHours')}</p>
                {(() => {
                  const dayOfWeek = new Date(changeForm.new_date).getDay();
                  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                  const dayName = dayNames[dayOfWeek];
                  const businessDay = businessWorkingHours[dayName];
                  const trainerDaySchedules = trainerSchedules[changeForm.trainer_id] || [];
                  const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
                  
                  return (
                    <>
                      <p className="text-xs text-blue-600">
                        {t("memberDashboard:businessHours")} {businessDay?.closed ? t("business:closed") : `${businessDay?.open || "08:00"} - ${businessDay?.close || "22:00"}`}
                      </p>
                      <p className="text-xs text-blue-600">
                        {t("memberDashboard:trainerHours")} {trainerDaySchedules.length > 0 
                          ? (trainerDaySchedule 
                              ? `${trainerDaySchedule.start_time} - ${trainerDaySchedule.end_time}` 
                              : t("memberDashboard:notWorkingToday"))
                          : t("memberDashboard:noScheduleDefined")}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Availability Status */}
            {(changeRoomAvailability || changeTrainerAvailability || changeMemberTimeConflict) && changeForm.new_start_time && (
              <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                <p className="font-medium text-navy text-sm">{t("common:availabilityStatus")}</p>
                
                {/* Member time conflict */}
                {changeMemberTimeConflict && (
                  <div className={`flex items-center gap-2 text-sm ${changeMemberTimeConflict.has_conflict ? "text-red-600" : "text-green-600"}`}>
                    {changeMemberTimeConflict.has_conflict ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>{t("memberDashboard:yourCalendar")} {changeMemberTimeConflict.has_conflict ? changeMemberTimeConflict.message : t("memberDashboard:thisTimeEmpty")}</span>
                  </div>
                )}
                
                {changeRoomAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${changeRoomAvailability.is_locked ? "text-red-600" : changeRoomAvailability.available_capacity > 0 ? "text-green-600" : "text-red-600"}`}>
                    {changeRoomAvailability.is_locked || changeRoomAvailability.available_capacity <= 0 ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>{t("memberDashboard:roomStatus")} {changeRoomAvailability.is_locked ? t("memberDashboard:locked") : changeRoomAvailability.available_capacity > 0 ? `${t("memberDashboard:available")} (${changeRoomAvailability.available_capacity} ${t("memberDashboard:personSlots")})` : t("reservations:lessonFull")}</span>
                  </div>
                )}
                
                {changeTrainerAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${changeTrainerAvailability.is_available ? "text-green-600" : "text-red-600"}`}>
                    {changeTrainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>
                      {t("memberDashboard:trainerStatus")} {changeTrainerAvailability.is_available 
                        ? (() => {
                            const info = changeTrainerAvailability.training_type_info;
                            if (info && info.max_participants > 1) {
                              return `${t("memberDashboard:available")} (${info.type} - ${info.remaining} ${t("memberDashboard:personSlots")})`;
                            }
                            return t("memberDashboard:available");
                          })()
                        : (changeTrainerAvailability.reason || t("memberDashboard:notAvailable"))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Change Reason */}
            {changeForm.new_start_time && (
              <div>
                <Label>{t("reservations:changeReasonLabel")}</Label>
                <Textarea
                  placeholder={t("common:ph_degisiklikNedeniniziYazin")}
                  value={changeForm.reason}
                  onChange={(e) => setChangeForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            )}

            <Button
              className="w-full bg-navy hover:bg-navy/90 rounded-xl"
              onClick={handleChangeRequest}
              disabled={
                !changeForm.new_date || 
                !changeForm.new_start_time ||
                !changeForm.trainer_id ||
                !changeForm.room_id ||
                sendingChangeRequest ||
                // Değişiklik yapılmadıysa buton pasif
                (selectedReservation && 
                  changeForm.new_date === selectedReservation.date &&
                  changeForm.new_start_time === selectedReservation.start_time &&
                  changeForm.trainer_id === selectedReservation.trainer_id &&
                  changeForm.room_id === selectedReservation.room_id
                ) ||
                (changeMemberTimeConflict && changeMemberTimeConflict.has_conflict) ||
                (changeTrainerAvailability && !changeTrainerAvailability.is_available) ||
                (changeRoomAvailability && (changeRoomAvailability.is_locked || changeRoomAvailability.available_capacity <= 0))
              }
            >
              {sendingChangeRequest && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {sendingChangeRequest ? t("memberDashboard:sending") : t("memberDashboard:sendChangeRequest")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Reservation Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              {t("memberDashboard:reservationCancel")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReservation && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-sm text-grey">{t("common:reservationToCancel")}</p>
                <p className="font-medium text-navy">
                  {selectedReservation.date} • {selectedReservation.start_time || selectedReservation.time}
                </p>
                <p className="text-sm text-grey">{getLocalizedBranch(selectedReservation.branch)} • {selectedReservation.trainer_name}</p>
                <p className="text-xs text-grey mt-1">{selectedReservation.business_name}</p>
              </div>
            )}

            {/* Cancellation Policy Warning */}
            {cancellationPolicy && (
              <div className={`p-3 rounded-xl ${isLateCancellation() ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-start gap-2">
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${isLateCancellation() ? 'text-red-500' : 'text-amber-500'}`} />
                  <div>
                    {isLateCancellation() ? (
                      <>
                        <p className="font-medium text-red-700">{t("common:gecİptalUyarisi")}</p>
                        <p className="text-sm text-red-600">
                          {t("memberDashboard:cancelPolicyWarning", {hours: cancellationPolicy.cancellation_window})}
                          {cancellationPolicy.late_cancel_penalty && (
                            cancellationPolicy.late_cancel_action === "burn_session" 
                              ?  t("memberDashboard:lateCancel1")
                              :  t("memberDashboard:lateCancel2")
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-amber-700">{t("common:i̇ptalPolitikasi")}</p>
                        <p className="text-sm text-amber-600">
                          {t("memberDashboard:cancelPolicyWarning", {hours: cancellationPolicy.cancellation_window})}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label>{t("memberDashboard:cancelReasonOptional")}</Label>
              <Textarea
                placeholder={t("memberDashboard:cancelReasonPh")}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="rounded-xl"
                data-testid="cancel-reason-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedReservation(null);
                  setCancelReason("");
                }}
              >
                {t("memberDashboard:giveUp")}
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                onClick={handleCancelReservation}
                disabled={cancelLoading}
                data-testid="confirm-cancel-btn"
              >
                {cancelLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {cancelLoading ? t("memberDashboard:cancelling") : isLateCancellation() ? t("memberDashboard:cancelAnyway") : t("memberDashboard:cancelReservation")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Request Modal - Full Form like New Request */}
      <Dialog open={showEditRequestModal} onOpenChange={(open) => {
        if (!open) {
          setShowEditRequestModal(false);
          setEditingRequest(null);
          setEditRequestType("");
          setEditSelectedPackageInfo(null);
        }
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-navy" />
              {t("memberDashboard:editRequestTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingRequest && (
              <>
                {/* İşletme Bilgisi - Salt Okunur */}
                <div className="p-3 bg-slate-100 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t("common:business")}</p>
                  <p className="font-semibold text-navy">
                    {memberships.find(m => m.business_id === editingRequest.business_id)?.business_name || editingRequest.business_name || t("memberDashboard:businessLabel")}
                  </p>
                </div>

                {/* Branch Selection */}
                {branches.length > 0 && (
                  <div>
                    <Label>{t("memberDashboard:branchLabel")}</Label>
                    <Select 
                      value={editRequestForm.branch} 
                      onValueChange={(v) => {
                        setEditRequestForm(prev => ({ ...prev, branch: v, member_package_id: "" }));
                        setEditSelectedPackageInfo(null);
                        setEditRequestType("");
                      }}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_bransSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Request Type: Package or Demo */}
                {editRequestForm.branch && (
                  <div>
                    <Label>{t("memberDashboard:requestType")}</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div
                        onClick={() => {
                          setEditRequestType("package");
                          setEditRequestForm(prev => ({ ...prev, member_package_id: "", is_demo: false }));
                          setEditSelectedPackageInfo(null);
                        }}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                          editRequestType === "package"
                            ? "border-navy bg-navy/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-semibold text-navy text-sm">{t("packages:package")}</p>
                        <p className="text-xs text-grey">{t("memberDashboard:fromExistingPackage")}</p>
                      </div>
                      <div
                        onClick={() => {
                          setEditRequestType("demo");
                          setEditRequestForm(prev => ({ ...prev, member_package_id: "", is_demo: true }));
                          setEditSelectedPackageInfo(null);
                        }}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                          editRequestType === "demo"
                            ? "border-green-600 bg-green-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-semibold text-green-700 text-sm">Demo</p>
                        <p className="text-xs text-grey">Deneme dersi</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Package Selection */}
                {editRequestType === "package" && (
                  <div>
                    <Label>{t("packages:package")} *</Label>
                    {memberPackages.filter(p => (p.is_active !== false && (p.status === 'active' || !p.status)) && p.remaining_sessions > 0 && 
                      (p.package_branches?.includes(editRequestForm.branch) || p.package_branch === editRequestForm.branch || !p.package_branch)
                    ).length > 0 ? (
                      <Select 
                        value={editRequestForm.member_package_id} 
                        onValueChange={(v) => {
                          setEditRequestForm(prev => ({ ...prev, member_package_id: v }));
                          const pkg = memberPackages.find(p => p.member_package_id === v);
                          setEditSelectedPackageInfo(pkg || null);
                        }}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder={t("packages:ph_paketSecin")} />
                        </SelectTrigger>
                        <SelectContent>
                          {memberPackages
                            .filter(p => (p.is_active !== false && (p.status === 'active' || !p.status)) && p.remaining_sessions > 0 && 
                              (p.package_branches?.includes(editRequestForm.branch) || p.package_branch === editRequestForm.branch || !p.package_branch)
                            )
                            .map((pkg) => (
                              <SelectItem key={pkg.member_package_id} value={pkg.member_package_id}>
                                {pkg.package_no ? `[${pkg.package_no}] ` : ""}{pkg.package_name} - {pkg.remaining_sessions} seans ({pkg.session_duration || 60} dk)
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-amber-600 text-sm mt-1 p-2 bg-amber-50 rounded-xl">
                        {t("memberDashboard:noBranchPackage")}
                      </p>
                    )}
                  </div>
                )}

                {/* Demo info */}
                {editRequestType === "demo" && (
                  <div className="bg-green-50 p-3 rounded-xl">
                    <p className="text-sm text-green-700">
                      <strong>{getLocalizedBranch(editRequestForm.branch)}</strong> {t("memberDashboard:requestedBranchDemo")}
                      <br />
                      <span className="text-xs">
                        {t("memberDashboard:demoDuration")} {branchDemoDurations[editRequestForm.branch] || 30} {t("memberDashboard:minutes")}
                      </span>
                    </p>
                  </div>
                )}

                {/* Trainer */}
                {((editRequestType === "package" && editRequestForm.member_package_id) || editRequestType === "demo") && (
                  <div>
                    <Label>{t("common:trainerRequired")}</Label>
                    <Select 
                      value={editRequestForm.trainer_id} 
                      onValueChange={(v) => setEditRequestForm(prev => ({ ...prev, trainer_id: v }))}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:selectTrainer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {trainers.filter(t => t.is_active || t.status === "active").map((t) => (
                          <SelectItem key={t.trainer_id} value={t.trainer_id}>
                            {t.name} {t.surname}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Room */}
                {editRequestForm.trainer_id && rooms.length > 0 && (
                  <div>
                    <Label>{t("memberDashboard:roomLabel")}</Label>
                    <Select 
                      value={editRequestForm.room_id} 
                      onValueChange={(v) => setEditRequestForm(prev => ({ ...prev, room_id: v }))}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_odaSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms
                          .filter(r => {
                            if (!r.is_active) return false;
                            if (editRequestForm.branch && r.branches && r.branches.length > 0) {
                              return r.branches.includes(editRequestForm.branch);
                            }
                            return true;
                          })
                          .map((r) => (
                            <SelectItem key={r.room_id} value={r.room_id}>
                              {r.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Date */}
                {editRequestForm.room_id && (
                  <div>
                    <Label>{t("common:dateRequired")}</Label>
                    <Popover open={editDatePickerOpen} onOpenChange={setEditDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal rounded-xl mt-1"
                        >
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {editRequestForm.date ? (
                            format(new Date(editRequestForm.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })
                          ) : (
                            <span className="text-muted-foreground">{t('common:selectDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                        <Calendar
                          mode="single"
                          selected={editRequestForm.date ? new Date(editRequestForm.date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              const formattedDate = format(date, "yyyy-MM-dd");
                              setEditRequestForm(prev => ({ ...prev, date: formattedDate, start_time: "" }));
                              setEditDatePickerOpen(false);
                            }
                          }}
                          locale={tr}
                          disabled={(date) => {
                            // Geçmiş günleri devre dışı bırak
                            if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                            // İşletme kapalı günleri devre dışı bırak
                            const dayOfWeek = date.getDay();
                            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                            const dayName = dayNames[dayOfWeek];
                            const businessDay = businessWorkingHours[dayName];
                            if (businessDay?.closed === true) return true;
                            return false;
                          }}
                          initialFocus={false}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                
                {/* Time */}
                {editRequestForm.date && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t("memberDashboard:startTime")}</Label>
                      {editValidTimeSlots.length > 0 ? (
                        <Select
                          value={editRequestForm.start_time}
                          onValueChange={(value) => {
                            setEditRequestForm(prev => {
                              let endTime = prev.end_time;
                              if (value) {
                                let duration = 60;
                                if (editRequestType === "demo") {
                                  duration = branchDemoDurations[editRequestForm.branch] || 30;
                                } else if (editSelectedPackageInfo) {
                                  duration = editSelectedPackageInfo.session_duration || 60;
                                }
                                const [h, m] = value.split(":").map(Number);
                                const endMinutes = h * 60 + m + duration;
                                const endH = Math.floor(endMinutes / 60);
                                const endM = endMinutes % 60;
                                endTime = `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;
                              }
                              return { ...prev, start_time: value, end_time: endTime };
                            });
                          }}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder={t("common:ph_saatSecin")} />
                          </SelectTrigger>
                          <SelectContent>
                            {editValidTimeSlots.map((slot) => (
                              <SelectItem key={slot} value={slot}>
                                {slot}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-3 bg-red-50 rounded-xl text-sm text-red-600">
                          {t("memberDashboard:noAvailableTimeForDate")}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>{t('settings:endTime')}</Label>
                      <div className="p-3 bg-slate-100 rounded-xl text-sm h-[42px] flex items-center">
                        {editRequestForm.end_time || "Otomatik"} 
                        {editRequestType === "package" && editSelectedPackageInfo?.session_duration && (
                          <span className="text-grey ml-1">({editSelectedPackageInfo.session_duration} dk)</span>
                        )}
                        {editRequestType === "demo" && editRequestForm.branch && (
                          <span className="text-grey ml-1">(Demo: {branchDemoDurations[editRequestForm.branch] || 30} dk)</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Working Hours Info */}
                {editRequestForm.date && editRequestForm.trainer_id && (
                  <div className="p-3 bg-blue-50 rounded-xl space-y-1">
                    <p className="font-medium text-blue-700 text-sm">{t('settings:workingHours')}</p>
                    {(() => {
                      const dayOfWeek = new Date(editRequestForm.date).getDay();
                      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                      const dayName = dayNames[dayOfWeek];
                      const businessDay = businessWorkingHours[dayName];
                      const trainerDaySchedules = trainerSchedules[editRequestForm.trainer_id] || [];
                      const trainerDaySchedule = trainerDaySchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
                      
                      return (
                        <>
                          <p className="text-xs text-blue-600">
                            {t("memberDashboard:businessHours")} {businessDay?.closed ? t("business:closed") : `${businessDay?.open || "08:00"} - ${businessDay?.close || "22:00"}`}
                          </p>
                          <p className="text-xs text-blue-600">
                            {t("memberDashboard:trainerHours")} {trainerDaySchedules.length > 0 
                              ? (trainerDaySchedule 
                                  ? `${trainerDaySchedule.start_time} - ${trainerDaySchedule.end_time}` 
                                  : t("memberDashboard:notWorkingToday"))
                              : t("memberDashboard:noScheduleDefined")}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Availability Status */}
                {(editRoomAvailability || editTrainerAvailability || editMemberTimeConflict) && editRequestForm.start_time && (
                  <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                    <p className="font-medium text-navy text-sm">{t("common:availabilityStatus")}</p>
                    
                    {editMemberTimeConflict && (
                      <div className={`flex items-center gap-2 text-sm ${editMemberTimeConflict.has_conflict ? "text-red-600" : "text-green-600"}`}>
                        {editMemberTimeConflict.has_conflict ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                        <span>{t("memberDashboard:yourCalendar")} {editMemberTimeConflict.has_conflict ? editMemberTimeConflict.message : t("memberDashboard:thisTimeEmpty")}</span>
                      </div>
                    )}
                    
                    {editRoomAvailability && (
                      <div className={`flex items-center gap-2 text-sm ${editRoomAvailability.is_locked ? "text-red-600" : editRoomAvailability.available_capacity > 0 ? "text-green-600" : "text-red-600"}`}>
                        {editRoomAvailability.is_locked || editRoomAvailability.available_capacity <= 0 ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                        <span>{t("memberDashboard:roomStatus")} {editRoomAvailability.is_locked ? t("memberDashboard:locked") : editRoomAvailability.available_capacity > 0 ? `${editRoomAvailability.available_capacity}/${editRoomAvailability.total_capacity} ${t("memberDashboard:available")}` : t("reservations:lessonFull")}</span>
                      </div>
                    )}
                    
                    {editTrainerAvailability && (
                      <div className={`flex items-center gap-2 text-sm ${editTrainerAvailability.is_available ? "text-green-600" : "text-red-600"}`}>
                        {editTrainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        <span>{t("memberDashboard:trainerStatus")} {editTrainerAvailability.is_available ? t("memberDashboard:available") : editTrainerAvailability.reason || t("memberDashboard:busy")}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Notes */}
                <div>
                  <Label>{t("common:noteOptional")}</Label>
                  <Textarea
                    value={editRequestForm.notes}
                    onChange={(e) => setEditRequestForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t("common:ph_eklemekIstediginizNot")}
                    className="rounded-xl"
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowEditRequestModal(false);
                  setEditingRequest(null);
                  setEditRequestType("");
                  setEditSelectedPackageInfo(null);
                  setEditRoomAvailability(null);
                  setEditTrainerAvailability(null);
                  setEditMemberTimeConflict(null);
                  setEditValidTimeSlots([]);
                }}
              >
                {t("memberDashboard:giveUp")}
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                disabled={
                  sendingEditRequest ||
                  !editRequestForm.date || 
                  !editRequestForm.start_time || 
                  !editRequestForm.trainer_id ||
                  (editMemberTimeConflict && editMemberTimeConflict.has_conflict) ||
                  (editTrainerAvailability && !editTrainerAvailability.is_available) ||
                  (editRoomAvailability && (editRoomAvailability.is_locked || editRoomAvailability.available_capacity <= 0))
                }
                onClick={async () => {
                  if (!editingRequest || sendingEditRequest) return;
                  
                  // Validate availability before submit
                  if (editMemberTimeConflict && editMemberTimeConflict.has_conflict) {
                    toast.error(editMemberTimeConflict.message || "Bu saatte zaten bir rezervasyonunuz var");
                    return;
                  }
                  
                  if (editTrainerAvailability && !editTrainerAvailability.is_available) {
                    toast.error(editTrainerAvailability.reason || t("memberDashboard:trainerNotAvailableAtTime"));
                    return;
                  }
                  
                  if (editRoomAvailability && editRoomAvailability.is_locked) {
                    toast.error(t('reservations:roomLockedAtTime'));
                    return;
                  }
                  
                  if (editRoomAvailability && editRoomAvailability.available_capacity <= 0) {
                    toast.error(t("memberDashboard:roomCapacityFull"));
                    return;
                  }
                  
                  setSendingEditRequest(true);
                  setPageLoading(true);
                  try {
                    // Eğer işletmeden değişiklik geldiyse, üye kendi değişikliğini gönderir
                    const endpoint = editingRequest.status === "modified_by_business"
                      ? `${API}/reservation-requests/${editingRequest.request_id}/member-modify`
                      : `${API}/reservation-requests/${editingRequest.request_id}`;
                    
                    const response = await fetch(endpoint, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({
                        ...editRequestForm,
                        is_demo: editRequestType === "demo"
                      })
                    });
                    
                    if (response.ok) {
                      const successMessage = editingRequest.status === "modified_by_business"
                        ? t("memberDashboard:changeSentToBusiness")
                        : t("memberDashboard:requestUpdated");
                      toast.success(successMessage);
                      setShowEditRequestModal(false);
                      setEditingRequest(null);
                      setEditRequestType("");
                      setEditSelectedPackageInfo(null);
                      setEditRoomAvailability(null);
                      setEditTrainerAvailability(null);
                      setEditMemberTimeConflict(null);
                      setEditValidTimeSlots([]);
                      fetchAllData();
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || t("memberDashboard:updateFailed"));
                    }
                  } catch (error) {
                    toast.error(t('common:errorOccurred'));
                  } finally {
                    setSendingEditRequest(false);
                    setPageLoading(false);
                  }
                }}
              >
                {sendingEditRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {sendingEditRequest ? t("memberDashboard:saving") : t("common:save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Request Modal */}
      <Dialog open={showCancelRequestModal} onOpenChange={(open) => {
        if (!open) {
          setShowCancelRequestModal(false);
          setCancellingRequest(null);
          setCancelRequestReason("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              {t("memberDashboard:cancelRequestTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cancellingRequest && (
              <>
                <div className="bg-slate-50 p-4 rounded-xl">
                  <p className="font-semibold text-navy">{getLocalizedBranch(cancellingRequest.branch) || t("memberDashboard:request")}</p>
                  <p className="text-sm text-grey">
                    {cancellingRequest.date ? format(new Date(cancellingRequest.date), "d MMMM yyyy", { locale: getDateLocale() }) : ""} • {cancellingRequest.start_time} - {cancellingRequest.end_time || ""}
                  </p>
                  <p className="text-xs text-grey mt-1">{cancellingRequest.trainer_name || ""}</p>
                </div>
                
                <div>
                  <Label className="text-red-600">{t("memberDashboard:cancelReasonRequired")}</Label>
                  <Textarea
                    value={cancelRequestReason}
                    onChange={(e) => setCancelRequestReason(e.target.value)}
                    placeholder={t("common:ph_i̇ptalNedeniniziYazin")}
                    className="rounded-xl mt-2"
                    rows={3}
                  />
                  <p className="text-xs text-grey mt-1">{t("memberDashboard:cancelReasonRequired")}</p>
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowCancelRequestModal(false);
                  setCancellingRequest(null);
                  setCancelRequestReason("");
                }}
              >
                {t("memberDashboard:giveUp")}
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl text-white"
                disabled={!cancelRequestReason.trim()}
                onClick={async () => {
                  if (!cancellingRequest || !cancelRequestReason.trim()) {
                    toast.error(t('members:cancellationReasonRequired'));
                    return;
                  }
                  
                  try {
                    const response = await fetch(`${API}/reservation-requests/${cancellingRequest.request_id}/cancel`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ reason: cancelRequestReason.trim() })
                    });
                    
                    if (response.ok) {
                      toast.success(t('common:requestCancelled'));
                      setShowCancelRequestModal(false);
                      setCancellingRequest(null);
                      setCancelRequestReason("");
                      fetchAllData();
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || t("memberDashboard:cancelFailed"));
                    }
                  } catch (error) {
                    toast.error(t('common:errorOccurred'));
                  }
                }}
              >
                {t("memberDashboard:cancelRequest")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Survey Modal */}
      <Dialog open={!!selectedSurvey} onOpenChange={(open) => {
        if (!open) {
          setSelectedSurvey(null);
          setSurveyAnswers({});
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-amber-600" />
              {selectedSurvey?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedSurvey?.description && (
              <p className="text-grey mb-4">{selectedSurvey.description}</p>
            )}
            
            <div className="space-y-6">
              {selectedSurvey?.questions?.map((question, index) => (
                <div key={question.question_id} className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span className="bg-navy/10 text-navy px-2 py-0.5 rounded text-sm">{index + 1}</span>
                    {question.text}
                    {question.is_required && <span className="text-red-500">*</span>}
                  </Label>
                  
                  {question.type === "text" && (
                    <Textarea
                      value={surveyAnswers[question.question_id] || ""}
                      onChange={(e) => setSurveyAnswers(prev => ({
                        ...prev,
                        [question.question_id]: e.target.value
                      }))}
                      placeholder={t("common:ph_cevabiniziYazin")}
                      className="rounded-xl"
                    />
                  )}
                  
                  {question.type === "yes_no" && (
                    <div className="flex gap-3">
                      {["Evet", "Hayır"].map((opt) => (
                        <Button
                          key={opt}
                          type="button"
                          variant={surveyAnswers[question.question_id] === opt ? "default" : "outline"}
                          className={`rounded-xl ${surveyAnswers[question.question_id] === opt ? "bg-navy" : ""}`}
                          onClick={() => setSurveyAnswers(prev => ({
                            ...prev,
                            [question.question_id]: opt
                          }))}
                        >
                          {opt}
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {question.type === "rating" && (
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <Button
                          key={num}
                          type="button"
                          variant={surveyAnswers[question.question_id] === num ? "default" : "outline"}
                          className={`w-10 h-10 rounded-xl ${surveyAnswers[question.question_id] === num ? "bg-navy" : ""}`}
                          onClick={() => setSurveyAnswers(prev => ({
                            ...prev,
                            [question.question_id]: num
                          }))}
                        >
                          {num}
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {question.type === "emoji" && (
                    <div className="flex gap-2">
                      {["😞", "😐", "🙂", "😊", "😍"].map((emoji, i) => (
                        <Button
                          key={emoji}
                          type="button"
                          variant={surveyAnswers[question.question_id] === (i + 1) ? "default" : "outline"}
                          className={`w-12 h-12 text-2xl rounded-xl ${surveyAnswers[question.question_id] === (i + 1) ? "bg-navy" : ""}`}
                          onClick={() => setSurveyAnswers(prev => ({
                            ...prev,
                            [question.question_id]: i + 1
                          }))}
                        >
                          {emoji}
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {question.type === "multiple_choice" && question.options?.map((opt) => (
                    <Button
                      key={opt}
                      type="button"
                      variant={surveyAnswers[question.question_id] === opt ? "default" : "outline"}
                      className={`mr-2 mb-2 rounded-xl ${surveyAnswers[question.question_id] === opt ? "bg-navy" : ""}`}
                      onClick={() => setSurveyAnswers(prev => ({
                        ...prev,
                        [question.question_id]: opt
                      }))}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedSurvey(null);
                setSurveyAnswers({});
              }}
              className="rounded-xl"
            >
              {t("memberDashboard:cancelSurvey")}
            </Button>
            <Button
              onClick={handleSubmitSurvey}
              disabled={submittingSurvey}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {submittingSurvey ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("memberDashboard:surveySubmitting")}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t("memberDashboard:submitSurvey")}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Evaluation Detail Modal */}
      <Dialog open={!!selectedEvaluation} onOpenChange={(open) => {
        if (!open) setSelectedEvaluation(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              {t("memberDashboard:evaluationDetail")}
            </DialogTitle>
          </DialogHeader>
          {selectedEvaluation && (
            <div className="py-4 space-y-4">
              {/* Title and Rating */}
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold text-navy">{selectedEvaluation.title}</h3>
                {selectedEvaluation.rating && (
                  <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`w-4 h-4 ${
                          star <= selectedEvaluation.rating 
                            ? "fill-yellow-400 text-yellow-400" 
                            : "text-slate-300"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Category Badge */}
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`${EVAL_CATEGORY_COLORS[selectedEvaluation.color] || "bg-navy text-white"}`}>
                  {selectedEvaluation.category === "general" && t("memberDashboard:categoryGeneral")}
                  {selectedEvaluation.category === "progress" && t("memberDashboard:categoryProgress")}
                  {selectedEvaluation.category === "technique" && t("memberDashboard:categoryTechnique")}
                  {selectedEvaluation.category === "nutrition" && t("memberDashboard:categoryNutrition")}
                  {selectedEvaluation.category === "motivation" && t("memberDashboard:categoryMotivation")}
                  {!selectedEvaluation.category && t("memberDashboard:categoryEvaluation")}
                </Badge>
                <Badge variant="outline" className="bg-slate-50 text-navy border-slate-200">
                  {format(new Date(selectedEvaluation.created_at), "d MMMM yyyy, HH:mm", { locale: getDateLocale() })}
                </Badge>
              </div>
              
              {/* Content */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-navy whitespace-pre-wrap">{selectedEvaluation.content}</p>
              </div>
              
              {/* Trainer Info */}
              <div className="flex items-center gap-3 pt-2 border-t">
                <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <p className="font-medium text-navy">{selectedEvaluation.trainer_name}</p>
                  <p className="text-sm text-grey">{selectedEvaluation.trainer_role || t("memberDashboard:trainerRole")}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setSelectedEvaluation(null)}
              className="rounded-xl"
            >
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Survey Response Preview Modal */}
      <Dialog open={!!viewingSurveyResponse} onOpenChange={(open) => !open && setViewingSurveyResponse(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-navy" />
              {t("memberDashboard:mySurveyResponses")}
            </DialogTitle>
          </DialogHeader>
          
          {loadingSurveyResponse ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy" />
            </div>
          ) : viewingSurveyResponse && (
            <div className="space-y-4">
              {/* Survey Info */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-lg text-navy mb-1">
                  {viewingSurveyResponse.survey_title || viewingSurveyResponse.title}
                </h3>
                <p className="text-sm text-grey">
                  {viewingSurveyResponse.survey_type_name || viewingSurveyResponse.type_name}
                </p>
                <p className="text-xs text-grey mt-2">
                  {t("memberDashboard:sentOn")} {viewingSurveyResponse.created_at || viewingSurveyResponse.submitted_at 
                    ? format(new Date(viewingSurveyResponse.created_at || viewingSurveyResponse.submitted_at), "d MMMM yyyy, HH:mm", { locale: getDateLocale() }) 
                    : "-"}
                </p>
              </div>
              
              {/* Answers */}
              <div className="space-y-4">
                {viewingSurveyResponse.questions?.map((q, index) => {
                  const answer = viewingSurveyResponse.answers?.[q.question_id] || viewingSurveyResponse.answers?.[index];
                  const EMOJIS = ["😢", "😕", "😐", "🙂", "😄"];
                  
                  return (
                    <div key={q.question_id || index} className="p-4 bg-white border rounded-xl">
                      <p className="font-medium text-navy mb-2">{index + 1}. {q.question || q.text}</p>
                      <div className="flex items-center gap-2">
                        {q.type === "rating" && answer && (
                          <>
                            <span className="text-2xl">{EMOJIS[Number(answer) - 1] || "❓"}</span>
                            <span className="text-navy font-semibold">{answer}/5</span>
                          </>
                        )}
                        {q.type === "yes_no" && (
                          <Badge className={answer === "Evet" ? "bg-green-100 text-green-700" : answer === "Hayır" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}>
                            {answer || "-"}
                          </Badge>
                        )}
                        {q.type === "text" && (
                          <p className="text-grey">{answer || "-"}</p>
                        )}
                        {q.type === "multiple_choice" && (
                          <Badge className="bg-navy text-white">{answer || "-"}</Badge>
                        )}
                        {(!q.type || !["rating", "yes_no", "text", "multiple_choice"].includes(q.type)) && (
                          <p className="text-grey">{answer || "-"}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* If no questions, show raw answers */}
                {!viewingSurveyResponse.questions && viewingSurveyResponse.answers && (
                  <div className="p-4 bg-white border rounded-xl">
                    <p className="text-sm text-grey mb-2">{t("memberDashboard:answers")}</p>
                    {Object.entries(viewingSurveyResponse.answers).map(([key, value], idx) => (
                      <div key={key} className="flex items-center gap-2 py-1">
                        <span className="text-navy font-medium">{idx + 1}.</span>
                        <span className="text-grey">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setViewingSurveyResponse(null)}
              className="rounded-xl"
            >
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Event Detail Modal */}
      <Dialog open={showEventDetailModal} onOpenChange={setShowEventDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-navy" />
              {t("memberDashboard:eventDetail")}
            </DialogTitle>
          </DialogHeader>
          
          {loadingEventDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy" />
            </div>
          ) : selectedEventDetail && (
            <div className="space-y-4">
              {/* Event Title & Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-navy">{selectedEventDetail.title}</h3>
                  <p className="text-sm text-grey mt-1">{selectedEventDetail.type_name}</p>
                </div>
                {selectedEventDetail.status === "completed" ? (
                  <Badge className="bg-blue-100 text-blue-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t("memberDashboard:completedEvent")}
                  </Badge>
                ) : selectedEventDetail.status === "cancelled" ? (
                  <Badge className="bg-red-100 text-red-700">
                    <XCircle className="w-3 h-3 mr-1" />
                    {t("memberDashboard:cancelledEvent")}
                  </Badge>
                ) : selectedEventDetail.is_joined ? (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t("memberDashboard:youAreJoined")}
                  </Badge>
                ) : selectedEventDetail.is_full ? (
                  <Badge className="bg-red-100 text-red-700">{t('reservations:full')}</Badge>
                ) : null}
              </div>
              
              {/* Price */}
              {!selectedEventDetail.is_free && selectedEventDetail.price > 0 && (
                <div className="p-3 bg-green-50 rounded-xl flex items-center justify-between">
                  <span className="font-medium text-green-700">{t("events:eventFee")}</span>
                  <span className="text-xl font-bold text-green-700">{currencySymbol}{selectedEventDetail.price}</span>
                </div>
              )}
              {selectedEventDetail.is_free && (
                <div className="p-3 bg-blue-50 rounded-xl flex items-center justify-between">
                  <span className="font-medium text-blue-700">{t("events:eventFee")}</span>
                  <span className="text-lg font-semibold text-blue-700">{t('common:free')}</span>
                </div>
              )}
              
              {/* Description */}
              {selectedEventDetail.description && (
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-navy whitespace-pre-wrap">{selectedEventDetail.description}</p>
                </div>
              )}
              
              {/* Event Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-grey mb-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="text-sm">{t('common:date')}</span>
                  </div>
                  <p className="font-medium text-navy">
                    {format(new Date(selectedEventDetail.date), "d MMMM yyyy", { locale: getDateLocale() })}
                  </p>
                  <p className="text-sm text-grey">
                    {format(new Date(selectedEventDetail.date), "EEEE", { locale: getDateLocale() })}
                  </p>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-grey mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{t('common:time')}</span>
                  </div>
                  <p className="font-medium text-navy">
                    {selectedEventDetail.start_time} - {selectedEventDetail.end_time}
                  </p>
                </div>
                
                {selectedEventDetail.location && (
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 text-grey mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{t('business:location')}</span>
                    </div>
                    <p className="font-medium text-navy">{selectedEventDetail.location}</p>
                  </div>
                )}
                
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-grey mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{t('reservations:participants')}</span>
                  </div>
                  <p className="font-medium text-navy">
                    {selectedEventDetail.participant_count}
                    {selectedEventDetail.max_participants ? ` / ${selectedEventDetail.max_participants}` : ""} {t("memberDashboard:person")}
                  </p>
                </div>
              </div>
              
              {/* Trainer */}
              {selectedEventDetail.trainer_name && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <p className="font-medium text-navy">{selectedEventDetail.trainer_name}</p>
                    <p className="text-sm text-grey">{t('common:trainer')}</p>
                  </div>
                </div>
              )}
              
              {/* Cancellation Reason */}
              {selectedEventDetail.status === "cancelled" && selectedEventDetail.cancellation_reason && (
                <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                  <p className="text-sm text-red-600 font-medium mb-1">{t("memberDashboard:cancellationReason")}</p>
                  <p className="text-red-700">{selectedEventDetail.cancellation_reason}</p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                {selectedEventDetail.status === "upcoming" && !selectedEventDetail.is_joined && !selectedEventDetail.is_full && (
                  <Button
                    onClick={() => {
                      handleJoinEvent(selectedEventDetail.event_id);
                      setShowEventDetailModal(false);
                    }}
                    className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {t("memberDashboard:join")}
                  </Button>
                )}
                {selectedEventDetail.status === "upcoming" && selectedEventDetail.is_joined && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleLeaveEvent(selectedEventDetail.event_id);
                      setShowEventDetailModal(false);
                    }}
                    className="flex-1 rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    {t("memberDashboard:leave")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowEventDetailModal(false)}
                  className="rounded-xl"
                >
                  {t("memberDashboard:close")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Existing Change Request Confirmation Modal */}
      <Dialog open={showExistingChangeConfirm} onOpenChange={setShowExistingChangeConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              {t("memberDashboard:existingRequestTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-grey mb-4">
              {t("memberDashboard:existingRequestDesc")}
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => {
                  setShowExistingChangeConfirm(false);
                  setPendingChangeReservation(null);
                }}
              >
                {t("memberDashboard:cancelBtn")}
              </Button>
              <Button
                className="rounded-xl bg-amber-500 hover:bg-amber-600"
                onClick={async () => {
                  setShowExistingChangeConfirm(false);
                  if (pendingChangeReservation) {
                    setSelectedReservation(pendingChangeReservation);
                    if (pendingChangeReservation.business_id) {
                      setLoadingBusinessData(true);
                      await fetchBusinessData(pendingChangeReservation.business_id);
                    }
                    setShowChangeModal(true);
                  }
                  setPendingChangeReservation(null);
                }}
              >
                {t("memberDashboard:updateRequest")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reservation Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-navy flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {t("memberDashboard:reservationDetail")}
            </DialogTitle>
          </DialogHeader>
          {selectedReservationDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey">{t('common:date')}</p>
                  <p className="font-semibold text-navy">
                    {selectedReservationDetail.date ? format(new Date(selectedReservationDetail.date), "d MMMM yyyy", { locale: getDateLocale() }) : "-"}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey">{t('common:time')}</p>
                  <p className="font-semibold text-navy">
                    {selectedReservationDetail.start_time} - {selectedReservationDetail.end_time}
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-grey">{t("common:branch")}</p>
                <p className="font-semibold text-navy">{getLocalizedBranch(selectedReservationDetail.branch) || "-"}</p>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-grey">{t('common:trainer')}</p>
                <p className="font-semibold text-navy">{selectedReservationDetail.trainer_name || "-"}</p>
              </div>
              
              {selectedReservationDetail.room_name && (
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey">{t('common:room')}</p>
                  <p className="font-semibold text-navy">{selectedReservationDetail.room_name}</p>
                </div>
              )}
              
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-grey">{t("common:business")}</p>
                <p className="font-semibold text-navy">{selectedReservationDetail.business_name || "-"}</p>
              </div>
              
              {selectedReservationDetail.package_name && (
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey">{t("packages:package")}</p>
                  <p className="font-semibold text-navy">{selectedReservationDetail.package_name}</p>
                </div>
              )}
              
              {selectedReservationDetail.is_demo && (
                <div className="p-3 bg-green-50 rounded-xl">
                  <Badge className="bg-green-100 text-green-700">{t("reservations:demoLesson")}</Badge>
                </div>
              )}
              
              {selectedReservationDetail.notes && (
                <div className="p-3 bg-blue-50 rounded-xl">
                  <p className="text-xs text-grey">{t('common:note')}</p>
                  <p className="text-sm text-navy">{selectedReservationDetail.notes}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button 
                  className="flex-1 rounded-xl bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/reservations/${selectedReservationDetail.reservation_id}/member-approve`, {
                        method: "PUT",
                        credentials: "include",
                      });
                      if (response.ok) {
                        toast.success(t('reservations:reservationApprovedSuccess'));
                        setShowDetailModal(false);
                        fetchAllData();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || t("memberDashboard:approveFailed"));
                      }
                    } catch (error) {
                      toast.error(t('common:errorOccurred'));
                    }
                  }}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Onayla
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/reservations/${selectedReservationDetail.reservation_id}/member-reject`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({ reason: t("memberDashboard:rejectedByMember") })
                      });
                      if (response.ok) {
                        toast.success(t('common:requestRejected'));
                        setShowDetailModal(false);
                        fetchAllData();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || t("memberDashboard:rejectFailed"));
                      }
                    } catch (error) {
                      toast.error(t('common:errorOccurred'));
                    }
                  }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reddet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Reservation Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-navy" />
              {t("memberDashboard:reservationDetails")}
            </DialogTitle>
          </DialogHeader>
          {viewingReservation && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-grey">{t("common:business")}</p>
                    <p className="font-semibold text-navy">{viewingReservation.business_name || t("memberDashboard:businessLabel")}</p>
                  </div>
                  {getStatusBadge(viewingReservation.status)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t('common:date')}</p>
                    <p className="font-medium">{viewingReservation.date ? format(new Date(viewingReservation.date), "dd.MM.yyyy EEEE", { locale: getDateLocale() }) : "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t('common:time')}</p>
                    <p className="font-medium">{viewingReservation.start_time || "-"} - {viewingReservation.end_time || "-"}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t("common:branch")}</p>
                    <p className="font-medium">{getLocalizedBranch(viewingReservation.branch) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t('common:trainer')}</p>
                    <p className="font-medium">{viewingReservation.trainer_name || "-"}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t('common:room')}</p>
                    <p className="font-medium">{viewingReservation.room_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t("reservations:requestType")}</p>
                    <p className="font-medium">
                      {viewingReservation.is_demo || viewingReservation.payment_type === "demo" 
                        ? "Demo" 
                        : viewingReservation.payment_type === "card" 
                          ? `${t("memberDashboard:card")}${viewingReservation.card_name ? ` (${viewingReservation.card_name})` : ""}`
                          : `${t("packages:package")}${viewingReservation.package_name ? ` (${viewingReservation.package_name})` : ""}`}
                    </p>
                  </div>
                </div>

                {viewingReservation.notes && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey">{t('common:note')}</p>
                    <p className="text-navy italic">&ldquo;{viewingReservation.notes}&rdquo;</p>
                  </div>
                )}

                {viewingReservation.previous_values && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey mb-1">{t("reservations:previousValues")}</p>
                    <p className="text-grey-600 line-through">
                      {viewingReservation.previous_values.date ? format(new Date(viewingReservation.previous_values.date), "dd.MM.yyyy", { locale: getDateLocale() }) : ""} {viewingReservation.previous_values.start_time || ""}
                      {viewingReservation.previous_values.trainer_name && ` • ${viewingReservation.previous_values.trainer_name}`}
                    </p>
                  </div>
                )}

                {viewingReservation.change_reason && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey">{t("reservations:changeReason")}</p>
                    <p className="text-purple-700 italic">&ldquo;{viewingReservation.change_reason}&rdquo;</p>
                  </div>
                )}
              </div>
              
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => setShowViewModal(false)}
              >
                Kapat
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
